import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EmojiPicker, { EmojiStyle, Theme as EmojiTheme } from "emoji-picker-react";
import { Avatar, EmptyState, StatusPill, Tabs } from "../../components/ui";
import { Icon } from "../shared/components";
import {
  API, ensureArray, ensureObject, formatTime, formatBrazilPhone,
  formatMinutesAsLabel, getSlaMeta, getConversationPriority,
  getInitials, whatsappSessionLabel, messageStatusLabel,
  conversationStatusLabel, renderTemplatePreview, renderQuickReplyBody,
  extractQuickReplyCommand, replaceQuickReplyCommand, windowStatusTone,
  normalizeBrazilPhone, maskBrazilPhoneInput, parseTemplateVariables,
  formatTemplateVariables, formatDateTimeFull, parseDBDate,
} from "../shared/utils";
import CustomerSidePanel from "./CustomerSidePanel";
import FinalizeModal from "./FinalizeModal";
import { useToast } from "../shared/toast";

function Metric({ title, value, hint }) { return <div className="metricCard"><span>{title}</span><strong>{value}</strong>{hint && <small>{hint}</small>}</div>; }

function formatLastSeen(isoStr) {
  if (!isoStr) return "";
  const d = parseDBDate(isoStr);
  if (isNaN(d)) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMs / 3_600_000);
  if (diffMin < 1)  return "agora mesmo";
  if (diffMin < 60) return `há ${diffMin} min`;
  if (diffH < 24) {
    return `hoje às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  }
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `ontem às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  }
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) +
         " às " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

// ── Message status tick (✓ ✓✓ 🔵✓✓) ─────────────────────────────────────────
function MessageStatusTick({ status, isOptimistic }) {
  if (isOptimistic || status === "sending") {
    return <span className="msgTick tick-sending" title="Enviando…">○</span>;
  }
  if (status === "error") {
    return <span className="msgTick tick-error" title="Erro ao enviar">!</span>;
  }
  if (status === "read") {
    return <span className="msgTick tick-read" title="Lida">✓✓</span>;
  }
  if (status === "delivered") {
    return <span className="msgTick tick-delivered" title="Entregue">✓✓</span>;
  }
  if (status === "sent" || status === "received" || status === "saved") {
    return <span className="msgTick tick-sent" title="Enviada">✓</span>;
  }
  return null;
}

// ── Conversation list helpers ─────────────────────────────────────────────────

const _AVATAR_COLORS = [
  "#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444","#14b8a6",
];
function getAvatarColor(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return _AVATAR_COLORS[Math.abs(hash) % _AVATAR_COLORS.length];
}

/** Retorna timestamp relativo legível: "agora", "3min", "2h", "ontem", "07/05" */
function formatRelativeTime(isoStr) {
  if (!isoStr) return "";
  const d = parseDBDate ? parseDBDate(isoStr) : new Date(isoStr);
  if (!d || isNaN(d)) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH   = Math.floor(diffMs / 3_600_000);
  if (diffMin < 1)  return "agora";
  if (diffMin < 60) return `${diffMin}min`;
  if (diffH < 24) {
    // Mesmo dia: mostrar hora absoluta estilo WhatsApp (14:32)
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "ontem";
  // Mais antigo: dd/mm
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/** Converte last_message em preview visual com ícone para mídia */
function formatMediaPreview(msg) {
  if (!msg) return "Sem mensagens";
  const m = String(msg).trim();
  if (m === "[áudio]"   || m === "[audio]")  return "🎤 Áudio";
  if (m === "[imagem]"  || m === "[image]")  return "📷 Imagem";
  if (m === "[vídeo]"   || m === "[video]")  return "🎥 Vídeo";
  if (m === "[documento]" || m === "[arquivo]" || m === "[file]") return "📄 Arquivo";
  if (m === "[mensagem]" || m === "[message]") return "💬 Mensagem";
  if (m === "[sticker]") return "🖼 Sticker";
  if (m === "[contato]" || m === "[contact]") return "👤 Contato";
  if (m === "[localização]" || m === "[location]") return "📍 Localização";
  // Detectar por prefixo (alguns sistemas gravam "Áudio: ..." etc.)
  if (m.startsWith("🎤") || m.startsWith("[🎤"))  return "🎤 Áudio";
  if (m.startsWith("📷") || m.startsWith("[📷"))  return "📷 Imagem";
  return m;
}

/** Retorna a classe de estado da barra lateral esquerda do item */
function getConvStateClass(conv) {
  if (conv.status === "finalizado") return "state-done";
  const unread = Number(conv.unread_count || 0);
  const waiting = Boolean(conv.waiting_for_reply);
  if (unread > 0 && waiting)  return "state-waiting-unread"; // vermelho: aguardando + não lido
  if (unread > 0)             return "state-unread";          // azul-brand: tem msg não lida
  if (waiting)                return "state-waiting-read";    // laranja: aguardando, já lido
  if (conv.status === "novo") return "state-novo";            // azul-info: nova conversa
  return "";
}

function calcWaitBadge(conv) {
  // Returns null or { label, level: "warn"|"danger" }
  const ts = conv.last_client_message_at;
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 15) return null;
  const diffH = Math.floor(diffMin / 60);
  const label = diffH >= 1
    ? `${diffH}h${diffMin % 60 > 0 ? (diffMin % 60) + "m" : ""} esperando`
    : `${diffMin}m esperando`;
  return { label, level: diffH >= 1 ? "danger" : "warn" };
}

// ── CSAT Modal ────────────────────────────────────────────────────────────────
function CsatModal({ conversation, api, onConfirm, onSkip }) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const labels = ["", "Muito ruim", "Ruim", "Regular", "Bom", "Excelente"];
  const emojis = ["", "😞", "😕", "😐", "🙂", "😄"];

  async function submit() {
    if (!rating) { onSkip(); return; }
    setSaving(true);
    try {
      await api.post(`/conversations/${conversation.id}/csat`, { rating, comment: comment || null });
    } catch {}
    finally { setSaving(false); }
    onConfirm();
  }

  return (
    <div className="modal-overlay-v2">
      <div className="modal-v2 modal-sm">
        <div className="modal-header">
          <h3>Como foi o atendimento?</h3>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-2)", marginBottom: 16 }}>
            Avalie a conversa com <strong>{conversation.contact_name || "este cliente"}</strong> antes de finalizar.
          </p>
          <div className="csatStars">
            {[1,2,3,4,5].map((n) => (
              <button
                key={n}
                className={`csatStar ${n <= (hovered || rating) ? "filled" : ""}`}
                onMouseEnter={() => setHovered(n)}
                onMouseLeave={() => setHovered(0)}
                onClick={() => setRating(n)}
                title={labels[n]}
              >★</button>
            ))}
          </div>
          {(hovered || rating) > 0 && (
            <div className="csatLabel">{emojis[hovered || rating]} {labels[hovered || rating]}</div>
          )}
          <textarea
            className="csatComment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Comentário opcional sobre o atendimento..."
            rows={3}
            style={{ marginTop: 12 }}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onSkip} disabled={saving}>Pular</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>
            {saving ? "Salvando…" : "Finalizar conversa"}
          </button>
        </div>
      </div>
    </div>
  );
}

const AUDIO_PATTERNS = ["[áudio]", "[audio]", "[voz]"];
const AUDIO_EXTS = /\.(ogg|opus|mp3|m4a|mp4|wav|webm)(\?|$)/i;
function isAudioMessage(msg) {
  if (!msg) return false;
  // Audio only if URL has audio extension (not just /api/media — images go there too)
  if (msg.media_url && AUDIO_EXTS.test(msg.media_url)) return true;
  const lower = (msg.message || "").toLowerCase().trim();
  return AUDIO_PATTERNS.some((p) => lower === p) && !msg.media_url;
}

/**
 * AudioBubble — reproduz áudio DENTRO do app via fetch→Blob URL.
 *
 * Por que fetch→Blob e não <audio src="..."> direto?
 *   - O <audio> cross-origin pode falhar por CORS em requests de Range
 *   - Usar src direto + link "Baixar" causava navegação da página
 *   - Com fetch, o JWT é passado como header (mais seguro que query param)
 *   - O Blob URL criado é same-origin → player nativo funciona sem restrição
 */
function AudioBubble({ mediaUrl }) {
  const [blobUrl, setBlobUrl]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [fetchError, setFetchError] = useState(false);

  // API URL with token for fallback download link (opens in new tab)
  const token = sessionStorage.getItem("safezap_token") || "";
  const remoteUrl = mediaUrl && mediaUrl.startsWith("/api/media")
    ? `${API}${mediaUrl}?token=${encodeURIComponent(token)}`
    : null;

  // Fetch audio as Blob → create a same-origin Object URL → play without CORS issues
  useEffect(() => {
    if (!remoteUrl) return;

    let objectUrl = null;
    const controller = new AbortController();

    setLoading(true);
    setFetchError(false);
    setBlobUrl(null);

    fetch(remoteUrl, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((err) => {
        if (err.name !== "AbortError") setFetchError(true);
      })
      .finally(() => setLoading(false));

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [remoteUrl]); // eslint-disable-line

  return (
    <div className="audioBubble">
      <div className="audioBubbleIcon"><Icon name="mic" size={14} /></div>

      {/* ── Audio ready: player nativo same-origin ── */}
      {blobUrl && (
        <audio
          className="audioBubblePlayer"
          controls
          preload="auto"
          src={blobUrl}
        />
      )}

      {/* ── Carregando blob ── */}
      {!blobUrl && loading && (
        <div className="audioBubbleLoading">
          <span /><span /><span />
        </div>
      )}

      {/* ── Erro ao buscar: abre em nova aba (não navega) ── */}
      {!blobUrl && !loading && fetchError && remoteUrl && (
        <div className="audioBubbleErrorRow">
          <span className="audioBubbleError">Erro ao carregar</span>
          <a
            href={remoteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="audioBubbleDownload"
            title="Abrir áudio em nova aba"
          >
            <Icon name="download" size={12} /> Baixar
          </a>
        </div>
      )}

      {/* ── Sem URL: download ainda não ocorreu ── */}
      {!remoteUrl && !loading && (
        <>
          <div className="audioBubbleWave">
            {[3, 7, 5, 9, 6, 10, 4, 8, 5, 7].map((h, i) => (
              <span key={i} style={{ "--bar-h": `${h}px`, animationDelay: `${i * 0.07}s` }} />
            ))}
          </div>
          <small className="audioBubbleLabel">Áudio não disponível</small>
        </>
      )}
    </div>
  );
}

// ── ImageBubble — fetch→Blob para evitar falha cross-origin em imagens ──────
function ImageBubble({ mediaUrl, onImageClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const token = sessionStorage.getItem("safezap_token") || "";
  const remoteUrl = mediaUrl && mediaUrl.startsWith("/api/")
    ? `${API}${mediaUrl}?token=${encodeURIComponent(token)}`
    : null;

  useEffect(() => {
    if (!remoteUrl) { setLoading(false); return; }
    let objectUrl = null;
    const controller = new AbortController();
    setLoading(true);
    setFetchError(false);
    setBlobUrl(null);
    fetch(remoteUrl, { signal: controller.signal })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => { objectUrl = URL.createObjectURL(blob); setBlobUrl(objectUrl); })
      .catch((err) => { if (err.name !== "AbortError") setFetchError(true); })
      .finally(() => setLoading(false));
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [remoteUrl]); // eslint-disable-line

  if (loading) return <div className="imageBubbleSkeleton" />;

  if (fetchError || !blobUrl) return (
    <div className="fileBubble">
      <div className="fileBubbleDoc"><Icon name="image" size={16} /><strong>imagem</strong></div>
      {remoteUrl && (
        <a href={remoteUrl} target="_blank" rel="noopener noreferrer" className="msgDownloadLink">
          <Icon name="download" size={11} /> Baixar imagem
        </a>
      )}
    </div>
  );

  return (
    <div className="imageBubbleWrap">
      <img
        src={blobUrl}
        alt="imagem"
        className="fileBubbleThumb"
        style={{ cursor: "zoom-in" }}
        onClick={() => onImageClick?.(blobUrl)}
      />
      <a href={remoteUrl} target="_blank" rel="noopener noreferrer" className="msgDownloadLink">
        <Icon name="download" size={11} /> Baixar imagem
      </a>
    </div>
  );
}

// ── Audio Recorder hook ────────────────────────────────────────────────────────
function useAudioRecorder() {
  const [state, setState] = useState("idle"); // idle | requesting | recording | recorded
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const reset = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      try { mediaRef.current.stop(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    mediaRef.current = null;
    chunksRef.current = [];
    setBlob(null);
    setPreviewUrl(null);
    setDuration(0);
    setState("idle");
  }, [previewUrl]);

  const start = useCallback(async () => {
    setState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer OGG/Opus; fallback to webm
      const mimeType = MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")
        ? "audio/ogg; codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm; codecs=opus")
          ? "audio/webm; codecs=opus"
          : "audio/webm";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const recorded = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(recorded);
        setBlob(recorded);
        setPreviewUrl(url);
        setState("recorded");
        stream.getTracks().forEach((t) => t.stop());
      };

      recorder.start(100);
      setState("recording");

      let secs = 0;
      timerRef.current = setInterval(() => {
        secs += 1;
        setDuration(secs);
        if (secs >= 120) stop(); // max 2 min
      }, 1000);
    } catch (err) {
      setState("idle");
      alert("Permissão de microfone negada ou indisponível.");
    }
  }, []);

  const stop = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRef.current && mediaRef.current.state === "recording") {
      mediaRef.current.stop();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => () => reset(), []); // eslint-disable-line

  return { state, blob, previewUrl, duration, start, stop, reset };
}

function AudioRecorderBar({ recorder, onSend, sending }) {
  const { state, previewUrl, duration, start, stop, reset } = recorder;

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (state === "idle") return null;

  return (
    <div className="audioRecorderBar">
      {(state === "recording" || state === "requesting") && (
        <>
          <span className="recDot" />
          <span className="recTimer">{fmt(duration)}</span>
          <span className="recLabel">Gravando…</span>
          <div className="recActions">
            <button className="ghostButton recCancelBtn" onClick={reset} title="Cancelar">
              <Icon name="close" size={14} /> Cancelar
            </button>
            <button className="primaryButton recStopBtn" onClick={stop} disabled={state === "requesting"}>
              <Icon name="stop" size={14} /> Parar
            </button>
          </div>
        </>
      )}
      {state === "recorded" && (
        <>
          <Icon name="mic" size={14} />
          <audio className="recPreviewPlayer" controls src={previewUrl} />
          <div className="recActions">
            <button className="ghostButton recCancelBtn" onClick={reset} title="Descartar">
              <Icon name="close" size={14} /> Descartar
            </button>
            <button className="primaryButton recSendBtn" onClick={onSend} disabled={sending}>
              <Icon name="send" size={14} /> {sending ? "Enviando…" : "Enviar"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const IMAGE_EXTS = /\.(jpg|jpeg|png|gif|webp)$/i;
const FILE_PATTERNS = ["[imagem]", "[arquivo:"];

function isImageMessage(msg) {
  if (!msg?.media_url) return false;
  return IMAGE_EXTS.test(msg.media_url);
}

function isFileMessage(msg) {
  if (!msg?.media_url) return false;
  const lower = (msg.message || "").toLowerCase();
  return FILE_PATTERNS.some((p) => lower.startsWith(p)) && !isAudioMessage(msg) && !isImageMessage(msg);
}

// ── WhatsApp markdown parser ──────────────────────────────────────────────────
function parseInlineMarkdown(text) {
  // Process inline tokens: bold, italic, strikethrough, code
  const parts = [];
  let remaining = text;
  let key = 0;

  // Order matters — code first (no nested), then bold/italic/strike
  const patterns = [
    { re: /`([^`]+)`/,        wrap: (s, k) => <code key={k} className="waCode">{s}</code> },
    { re: /\*([^*]+)\*/,      wrap: (s, k) => <strong key={k}>{s}</strong> },
    { re: /_([^_]+)_/,        wrap: (s, k) => <em key={k}>{s}</em> },
    { re: /~([^~]+)~/,        wrap: (s, k) => <del key={k}>{s}</del> },
  ];

  while (remaining.length > 0) {
    let earliest = null;
    let earliestIndex = Infinity;

    for (const { re, wrap } of patterns) {
      const m = re.exec(remaining);
      if (m && m.index < earliestIndex) {
        earliest = { m, wrap };
        earliestIndex = m.index;
      }
    }

    if (!earliest) {
      parts.push(remaining);
      break;
    }

    const { m, wrap } = earliest;
    if (m.index > 0) parts.push(remaining.slice(0, m.index));
    parts.push(wrap(m[1], key++));
    remaining = remaining.slice(m.index + m[0].length);
  }

  return parts;
}

function parseWaMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, i) => {
    if (line.startsWith("> ")) {
      return <blockquote key={i} className="waQuote">{parseInlineMarkdown(line.slice(2))}</blockquote>;
    }
    if (line === "") return <br key={i} />;
    return <span key={i} className="waLine">{parseInlineMarkdown(line)}{i < lines.length - 1 && "\n"}</span>;
  });
}

function DeletedMessageBubble({ msg, isAdmin }) {
  const [revealed, setRevealed] = React.useState(false);
  return (
    <div className="deletedMsgBubble">
      <span className="deletedMsgIcon">🚫</span>
      <span className="deletedMsgText">Esta mensagem foi apagada</span>
      {isAdmin && msg.message && (
        revealed ? (
          <span className="deletedMsgReveal revealedText">{msg.message}</span>
        ) : (
          <button
            type="button"
            className="deletedMsgRevealBtn"
            onClick={() => setRevealed(true)}
            title="Ver conteúdo (apenas visível para admins)"
          >
            👁 ver mensagem
          </button>
        )
      )}
    </div>
  );
}

function MessageContent({ msg, onImageClick, isAdmin }) {
  const token = sessionStorage.getItem("safezap_token") || "";

  // Soft-deleted: show placeholder
  if (msg.is_deleted) {
    return <DeletedMessageBubble msg={msg} isAdmin={isAdmin} />;
  }

  if (isAudioMessage(msg)) return <AudioBubble mediaUrl={msg.media_url} />;

  if (isImageMessage(msg)) {
    return <ImageBubble mediaUrl={msg.media_url} onImageClick={onImageClick} />;
  }

  if (isFileMessage(msg)) {
    const src      = `${API}${msg.media_url}?token=${encodeURIComponent(token)}`;
    const filename = msg.media_url.split("/").pop() || "arquivo";
    const label    = (msg.message || "").replace("[arquivo: ", "").replace("]", "") || filename;
    return (
      <div className="fileBubble">
        <div className="fileBubbleDoc">
          <Icon name="attach" size={16} />
          <strong>{label}</strong>
        </div>
        <a href={src} download className="msgDownloadLink">
          <Icon name="download" size={11} /> Baixar arquivo
        </a>
      </div>
    );
  }

  return <p className="waMsgText">{parseWaMarkdown(msg.message)}</p>;
}

// ── Message context menu ──────────────────────────────────────────────────────
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "🙏", "✉️"];

function MsgContextMenu({ ctxMenu, isOwn, isAdmin, isPinned, isStarred, onClose, onReact, onReply, onCopy, onDelete, onForward, onPin, onFavorite, onEdit, onInfo }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: ctxMenu.y, left: ctxMenu.x });

  // Clamp position so the menu never exits the viewport
  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = ctxMenu.x;
    let top  = ctxMenu.y;
    if (left + rect.width  > vw - 8) left = vw - rect.width  - 8;
    if (left < 8) left = 8;
    if (top  + rect.height > vh - 8) top  = ctxMenu.y - rect.height - 28;
    if (top  < 8) top  = 8;
    setPos({ top, left });
  }, [ctxMenu.x, ctxMenu.y]);

  // Close on outside click or Escape
  useEffect(() => {
    function onDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    }
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown",   onKey);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown",   onKey);
    };
  }, [onClose]);

  const items = [
    { key: "reply",   icon: "reply",   label: "Responder",                           action: onReply   },
    { key: "copy",    icon: "copy",    label: "Copiar texto",                         action: onCopy    },
    { key: "forward", icon: "forward", label: "Encaminhar",                           action: onForward },
    { key: "pin",     icon: "pin",     label: isPinned ? "Desafixar mensagem" : "Fixar mensagem", action: onPin },
    { key: "star",    icon: "star",    label: isStarred ? "Desfavoritar"     : "Favoritar",       action: onFavorite },
    { key: "info",    icon: "info",    label: "Dados da mensagem",                   action: onInfo    },
    ...(isOwn   ? [{ key: "edit",   icon: "edit",  label: "Editar",  action: onEdit,   danger: false }] : []),
    ...(isOwn || isAdmin ? [{ key: "delete", icon: "trash", label: "Apagar", action: onDelete, danger: true  }] : []),
  ];

  return (
    <div ref={menuRef} className="msgCtxMenu" style={{ top: pos.top, left: pos.left }}>
      <div className="msgCtxReactions">
        {QUICK_REACTIONS.map((r) => (
          <button key={r} type="button" className="msgCtxReactionBtn" onClick={() => { onReact(r); onClose(); }}>
            {r}
          </button>
        ))}
      </div>
      <div className="msgCtxDivider" />
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`msgCtxItem${item.danger ? " msgCtxItemDanger" : ""}`}
          onClick={() => { item.action(); onClose(); }}
        >
          <Icon name={item.icon} size={14} />
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Message info modal ────────────────────────────────────────────────────────
function MsgInfoModal({ msg, onClose }) {
  if (!msg) return null;
  return (
    <div className="modal-overlay-v2" onClick={onClose}>
      <div className="modal-v2 modal-sm msgInfoModal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Dados da mensagem</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
        <div className="modal-body">
          <div className="msgInfoRow">
            <span>De</span>
            <strong>{msg.sender_type === "cliente" ? "Cliente" : (msg.sender_user_name || "Atendente")}</strong>
          </div>
          <div className="msgInfoRow">
            <span>Enviado em</span>
            <strong>{parseDBDate(msg.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" })}</strong>
          </div>
          {msg.status && (
            <div className="msgInfoRow">
              <span>Status</span>
              <strong>{messageStatusLabel(msg.status)}</strong>
            </div>
          )}
          {msg.message && (
            <div className="msgInfoRow msgInfoRowMsg">
              <span>Conteúdo</span>
              <p>{msg.message}</p>
            </div>
          )}
          {msg.media_url && (
            <div className="msgInfoRow">
              <span>Mídia</span>
              <strong style={{ wordBreak: "break-all", fontSize: 11 }}>{msg.media_url}</strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Forward modal ─────────────────────────────────────────────────────────────
function ForwardModal({ msg, conversations, onForward, onClose }) {
  const [target, setTarget] = useState("");
  const [sending, setSending] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = conversations
    .filter((c) => c.id !== msg?.conversation_id)
    .filter((c) => !query || (c.contact_name || "").toLowerCase().includes(query.toLowerCase()))
    .slice(0, 25);

  async function doForward() {
    if (!target || sending) return;
    setSending(true);
    try {
      await onForward(msg, Number(target));
      onClose();
    } catch {
      setSending(false);
    }
  }

  return (
    <div className="modal-overlay-v2" onClick={onClose}>
      <div className="modal-v2 modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Encaminhar mensagem</h3>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
        <div className="modal-body" style={{ gap: 10 }}>
          {msg?.message && (
            <div className="forwardMsgPreview">
              <Icon name="forward" size={12} />
              <span>{msg.message.slice(0, 80)}{msg.message.length > 80 ? "…" : ""}</span>
            </div>
          )}
          <input
            className="finModal-input"
            placeholder="Buscar conversa…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="forwardConvList">
            {filtered.length === 0 ? (
              <p style={{ fontSize: "var(--fs-xs)", color: "var(--text-3)", textAlign: "center", padding: "16px 0" }}>Nenhuma conversa encontrada.</p>
            ) : filtered.map((c) => (
              <div
                key={c.id}
                className={`forwardConvItem${String(target) === String(c.id) ? " selected" : ""}`}
                onClick={() => setTarget(String(c.id))}
              >
                <div className="forwardConvAvatar">{(c.contact_name || "?").slice(0, 2).toUpperCase()}</div>
                <div className="forwardConvInfo">
                  <strong>{c.contact_name || "Sem nome"}</strong>
                  <span>{(c.last_message || "").slice(0, 45) || "—"}</span>
                </div>
                {String(target) === String(c.id) && <Icon name="check" size={14} style={{ color: "var(--brand-500)", flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-primary btn-sm" onClick={doForward} disabled={sending || !target}>
            {sending ? "Encaminhando…" : "Encaminhar"}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function AttendancePage({ api, user, onActiveConversationChange, onUnreadChange, setActivePage, initialConversationId, clearInitialConversation }) {
  const { showToast } = useToast();
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sessionStatus, setSessionStatus] = useState({ status: "disconnected", phone: null, qr: null });
  const [text, setText] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendMode, setSendMode] = useState("whatsapp");
  const [sendChannel, setSendChannel] = useState({ ready: false, mode: "unknown", error: "", session_status: "unknown" });
  const [sendNotice, setSendNotice] = useState("");
  const [sending, setSending] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("normal"); // "normal" | "groups"
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("safezap_sort_order") || "recent"); // "recent" | "priority"
  const [autoAssignStrategy, setAutoAssignStrategy] = useState("lowest_load");
  const [assigningAutomatically, setAssigningAutomatically] = useState(false);
  const [autoOpenFirst, setAutoOpenFirst] = useState(() => {
    const stored = localStorage.getItem("safezap_auto_open_first");
    return stored === null ? true : stored === "1";
  });
  const [panelRefreshToken, setPanelRefreshToken] = useState(0);
  const [windowStatus, setWindowStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateDraft, setTemplateDraft] = useState({ template_id: "", variables: "" });
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const composerTextareaRef = useRef(null);

  function insertEmoji(emojiData) {
    const ta = composerTextareaRef.current;
    const emoji = emojiData?.emoji || "";
    if (!emoji) return;
    if (ta) {
      const start = ta.selectionStart ?? text.length;
      const end = ta.selectionEnd ?? text.length;
      const next = text.slice(0, start) + emoji + text.slice(end);
      setText(next);
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + emoji.length;
        ta.setSelectionRange(pos, pos);
      });
    } else {
      setText((t) => t + emoji);
    }
  }
  const [quickReplies, setQuickReplies] = useState([]);
  const [quickReplySearch, setQuickReplySearch] = useState("");
  const [quickReplyCategory, setQuickReplyCategory] = useState("all");
  const [quickReplyOpen, setQuickReplyOpen] = useState(false);
  const [quickReplyHighlightIndex, setQuickReplyHighlightIndex] = useState(0);
  const [mobilePanel, setMobilePanel] = useState("list"); // "list" | "chat" | "crm"
  // Transfer conversation
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferring, setTransferring] = useState(false);
  // Archive conversation
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // 3-dot dropdown menu
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [moreMenuPos, setMoreMenuPos] = useState({ top: 0, right: 0 });
  const moreMenuRef = useRef(null);
  const moreBtnRef = useRef(null);
  // In-conversation search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  // CRM side panel visibility (desktop toggle)
  const [crmPanelOpen, setCrmPanelOpen] = useState(true);
  // File attachment
  const [attachFile, setAttachFile] = useState(null); // { file, previewUrl, isImage }
  const [sendingFile, setSendingFile] = useState(false);
  const attachInputRef = useRef(null);
  // Scroll anchor
  const messagesEndRef = useRef(null);
  // Scrollable messages container
  const chatBodyRef = useRef(null);
  // Scroll-to-bottom floating button
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [scrollUnread, setScrollUnread] = useState(0);
  const prevMsgCountRef = useRef(0);
  const prevConvIdRef   = useRef(null);
  // Message pagination
  const [hasMoreMsgs, setHasMoreMsgs] = useState(false);
  const [loadingOlderMsgs, setLoadingOlderMsgs] = useState(false);
  // Suppress bottom-scroll when loading older messages (prepend)
  const skipScrollRef = useRef(false);
  // Finalize modal
  const [finalizePending, setFinalizePending] = useState(null); // conversation object waiting to be finalized
  // Safe IA
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiData, setAiData] = useState(null);
  // Cursor-based pagination
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Cross-conversation message search
  const [msgSearchResults, setMsgSearchResults] = useState(null); // null = not in search mode
  const [msgSearchLoading, setMsgSearchLoading] = useState(false);
  // Message context menu
  const [ctxMenu, setCtxMenu]           = useState(null); // { msg, x, y }
  const [replyTo, setReplyTo]           = useState(null); // message being replied to
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [msgInfoMsg, setMsgInfoMsg]     = useState(null); // message info modal
  const [editingMsg, setEditingMsg]     = useState(null); // message being edited
  const [forwardModal, setForwardModal] = useState(null); // message to forward
  const [fmtBarVisible, setFmtBarVisible] = useState(false); // formatting toolbar
  // Contact presence (online / last seen)
  const [contactPresence, setContactPresence] = useState(null); // { is_online, last_seen_at }
  const presenceTimerRef = useRef(null);
  // Typing indicator
  const [isContactTyping, setIsContactTyping] = useState(false);
  const typingTimerRef = useRef(null);
  // Delete confirmation
  const [deleteConfirmMsg, setDeleteConfirmMsg] = useState(null);

  const selectedRef = useRef(null);
  const audioRecorder = useAudioRecorder();
  const streamRef = useRef(null);
  const reconnectRef = useRef(null);
  const refreshStateRef = useRef({ conversations: false, selected: false, summary: false, session: false });
  // IDs de mensagens que o próprio usuário acabou de enviar — evita re-fetch desnecessário via SSE
  const recentlySentIdsRef = useRef(new Set());

  async function loadConversations(append = false) {
    try {
      if (append && !nextCursor) return;
      if (append) setLoadingMore(true);

      const params = append && nextCursor ? { cursor: nextCursor, limit: 50 } : { limit: 50 };
      const response = await api.get("/conversations", { params });
      const data = response.data;

      // Support both legacy array and new paginated format
      const list = Array.isArray(data) ? data : ensureArray(data.conversations);
      const totalUnread = Array.isArray(data)
        ? list.filter((i) => Number(i.unread_count || 0) > 0).length
        : (data.total_unread || 0);

      setConversations((prev) => append ? [...prev, ...list] : list);
      setHasMore(Array.isArray(data) ? false : Boolean(data.has_more));
      setNextCursor(Array.isArray(data) ? null : (data.next_cursor || null));
      onUnreadChange?.(totalUnread);
      setFetchError("");

      if (selected && !append) {
        const updated = list.find((item) => item.id === selected.id);
        if (updated) setSelected(updated);
      }
    } catch (err) {
      setFetchError("Não foi possível carregar as conversas. Verifique a conexão com o servidor.");
    } finally {
      if (append) setLoadingMore(false);
    }
  }

  async function searchMessages(q) {
    const term = q.trim();
    if (term.length < 2) { setMsgSearchResults(null); return; }
    setMsgSearchLoading(true);
    try {
      const res = await api.get("/conversations/search-messages", { params: { q: term, limit: 20 } });
      setMsgSearchResults(ensureArray(res.data));
    } catch {
      setMsgSearchResults([]);
    } finally {
      setMsgSearchLoading(false);
    }
  }
  async function loadUsers() {
    try {
      const response = await api.get("/users");
      setUsers(ensureArray(response.data));
    } catch {
      setUsers([]);
    }
  }
  async function loadSummary() {
    try {
      const response = await api.get("/reports/summary");
      setSummary(ensureObject(response.data));
    } catch {
      setSummary(null);
    }
  }
  async function loadWhatsappSession() {
    try {
      const response = await api.get("/whatsapp/session");
      setSessionStatus(ensureObject(response.data) || { status: "disconnected", phone: null, qr: null });
    } catch (err) {
      setSessionStatus({ status: "disconnected", phone: null, qr: null });
    }
  }

  async function loadSendChannel() {
    try {
      const response = await api.get("/whatsapp/send-status");
      const payload = ensureObject(response.data) || { ready: false, mode: "unknown", error: "", session_status: "unknown" };
      setSendChannel(payload);
      return payload;
    } catch (err) {
      const fallback = { ready: false, mode: "unknown", error: "Não foi possível validar o canal de envio.", session_status: "unknown" };
      setSendChannel(fallback);
      return fallback;
    }
  }

  async function loadTemplates() {
    try {
      const response = await api.get("/whatsapp-templates");
      setTemplates(ensureArray(response.data).filter((item) => item?.status === "approved"));
    } catch {
      setTemplates([]);
    }
  }

  async function loadQuickReplies() {
    try {
      const response = await api.get("/quick-replies");
      setQuickReplies(ensureArray(response.data));
    } catch {
      setQuickReplies([]);
    }
  }

  async function loadContactForConversation(conversation) {
    try {
      const response = await api.get(`/contacts/${conversation.contact_id}`);
      setSelectedContact(ensureObject(response.data));
    } catch {
      setSelectedContact(null);
    }
  }

  async function loadConversationWindow(conversation) {
    if (!conversation?.id) {
      setWindowStatus(null);
      return;
    }
    try {
      const response = await api.get(`/conversations/${conversation.id}/window`);
      setWindowStatus(ensureObject(response.data));
    } catch {
      setWindowStatus(null);
    }
  }

  async function refreshSelectedConversation(conversation, options = {}) {
    const { showLoader = false, refreshContact = false } = options;
    if (!conversation) return;
    setSelected(conversation);
    // Reset typing state when switching conversations
    setIsContactTyping(false);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (showLoader) setLoadingMessages(true);
    if (refreshContact) await loadContactForConversation(conversation);
    await loadConversationWindow(conversation);
    try {
      const response = await api.get(`/conversations/${conversation.id}/messages`, { params: { limit: 50 } });
      const data = response.data;
      // Support both legacy array and new paginated { messages, has_more }
      if (Array.isArray(data)) {
        setMessages(data);
        setHasMoreMsgs(false);
      } else {
        setMessages(ensureArray(data.messages));
        setHasMoreMsgs(Boolean(data.has_more));
      }
      setFetchError("");
    } catch (err) {
      setFetchError("Não foi possível carregar as mensagens.");
    } finally {
      if (showLoader) setLoadingMessages(false);
    }
  }

  async function loadMessages(conversation, options = {}) {
    const { silent = false, refreshContact = true } = options;
    await refreshSelectedConversation(conversation, { showLoader: !silent, refreshContact });
    if (Number(conversation.unread_count || 0) > 0) {
      await markConversationRead(conversation.id, true);
    }
    // Load contact presence when opening a conversation
    loadPresence(conversation.id);
    // Auto-focus the composer so the user can type immediately after selecting
    setTimeout(() => composerTextareaRef.current?.focus(), 80);
  }

  async function loadPresence(convId) {
    try {
      const res = await api.get(`/conversations/${convId}/presence`);
      setContactPresence(res.data);
    } catch { setContactPresence(null); }
    // Ask backend to subscribe to Baileys presence events (typing indicators)
    api.post(`/conversations/${convId}/subscribe-presence`).catch(() => {});
    // Refresh presence every 60s while conversation is open
    if (presenceTimerRef.current) clearTimeout(presenceTimerRef.current);
    presenceTimerRef.current = setTimeout(() => {
      if (selectedRef.current && Number(selectedRef.current.id) === Number(convId)) {
        loadPresence(convId);
      }
    }, 60_000);
  }

  async function loadOlderMessages() {
    if (!selected || loadingOlderMsgs || !hasMoreMsgs) return;
    const oldestId = messages[0]?.id;
    if (!oldestId) return;

    const container = chatBodyRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    skipScrollRef.current = true;
    setLoadingOlderMsgs(true);
    try {
      const response = await api.get(`/conversations/${selected.id}/messages`, {
        params: { limit: 50, before: oldestId },
      });
      const data = response.data;
      const older = Array.isArray(data) ? data : ensureArray(data.messages);
      const moreAvail = Array.isArray(data) ? false : Boolean(data.has_more);

      setMessages((prev) => [...older, ...prev]);
      setHasMoreMsgs(moreAvail);

      // Restore scroll so the user stays at the same visual position
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
        skipScrollRef.current = false;
      });
    } catch {
      skipScrollRef.current = false;
    } finally {
      setLoadingOlderMsgs(false);
    }
  }

  // ── Context menu handlers ──────────────────────────────────────
  function openCtxMenu(e, msg) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setCtxMenu({ msg, x: rect.left, y: rect.bottom + 4 });
  }
  function closeCtxMenu() { setCtxMenu(null); }

  function handleCtxCopy(msg) {
    const content = msg.message || "";
    if (!content) {
      showToast({ variant: "info", title: "Sem texto", message: "Esta mensagem não tem texto para copiar." });
      return;
    }
    navigator.clipboard?.writeText(content)
      .then(() => showToast({ variant: "success", title: "Copiado!", message: "Texto copiado para a área de transferência." }))
      .catch(() => showToast({ variant: "danger", title: "Erro", message: "Não foi possível copiar o texto." }));
  }

  function handleCtxReply(msg) {
    setReplyTo(msg);
    composerTextareaRef.current?.focus();
  }

  function handleCtxDelete(msg) {
    setDeleteConfirmMsg(msg); // show confirmation modal
  }

  async function confirmDelete() {
    const msg = deleteConfirmMsg;
    if (!msg || !selected) return;
    setDeleteConfirmMsg(null);
    try {
      await api.delete(`/conversations/${selected.id}/messages/${msg.id}`);
      // Soft-delete: mark locally (SSE will also arrive)
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, is_deleted: 1 } : m));
      showToast({ variant: "success", title: "Apagada", message: "Mensagem apagada." });
    } catch (err) {
      showToast({ variant: "danger", title: "Erro", message: err?.response?.data?.error || "Não foi possível apagar a mensagem." });
    }
  }

  function handleCtxStub(label) {
    showToast({ variant: "info", title: label, message: "Funcionalidade em breve disponível." });
  }

  // ── Reaction ────────────────────────────────────────────────────
  async function handleCtxReact(msg, emoji) {
    if (!selected) return;
    try {
      const res = await api.post(`/conversations/${selected.id}/messages/${msg.id}/react`, { emoji });
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, reactions_json: JSON.stringify(res.data.reactions) } : m));
    } catch {
      showToast({ variant: "danger", title: "Erro", message: "Não foi possível reagir à mensagem." });
    }
  }

  // ── Pin ─────────────────────────────────────────────────────────
  async function handleCtxPin(msg) {
    if (!selected) return;
    try {
      const res = await api.patch(`/conversations/${selected.id}/messages/${msg.id}/pin`);
      const pinned = !!res.data.is_pinned;
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, is_pinned: res.data.is_pinned } : m));
      showToast({ variant: "success", title: pinned ? "📌 Fixado" : "Desfixado", message: pinned ? "Mensagem fixada no chat." : "Mensagem removida dos fixados." });
    } catch {
      showToast({ variant: "danger", title: "Erro", message: "Não foi possível fixar a mensagem." });
    }
  }

  // ── Star ────────────────────────────────────────────────────────
  async function handleCtxStar(msg) {
    if (!selected) return;
    try {
      const res = await api.patch(`/conversations/${selected.id}/messages/${msg.id}/star`);
      const starred = !!res.data.is_starred;
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, is_starred: res.data.is_starred } : m));
      showToast({ variant: "success", title: starred ? "⭐ Favoritado" : "Desfavoritado", message: "" });
    } catch {
      showToast({ variant: "danger", title: "Erro", message: "Não foi possível favoritar." });
    }
  }

  // ── Edit ────────────────────────────────────────────────────────
  function handleCtxEdit(msg) {
    setEditingMsg(msg);
    setText(msg.message || "");
    setTimeout(() => composerTextareaRef.current?.focus(), 50);
  }

  function cancelEdit() {
    setEditingMsg(null);
    setText("");
  }

  async function saveEdit() {
    if (!editingMsg || !selected || !text.trim()) return;
    try {
      const res = await api.patch(`/conversations/${selected.id}/messages/${editingMsg.id}`, { message: text.trim() });
      setMessages((prev) => prev.map((m) => m.id === editingMsg.id ? { ...m, ...res.data } : m));
      setEditingMsg(null);
      setText("");
      showToast({ variant: "success", title: "Mensagem editada", message: "" });
    } catch (err) {
      showToast({ variant: "danger", title: "Erro ao editar", message: err?.response?.data?.error || "Tente novamente." });
    }
  }

  // ── Forward ─────────────────────────────────────────────────────
  async function handleForward(msg, targetConvId) {
    if (!selected) return;
    await api.post(`/conversations/${selected.id}/messages/${msg.id}/forward`, { target_conversation_id: targetConvId });
    showToast({ variant: "success", title: "↪ Encaminhado", message: "Mensagem copiada para a conversa destino." });
  }
  // ──────────────────────────────────────────────────────────────

  async function sendMessage(e) {
    e.preventDefault();
    if (!selected || !text.trim() || sending) return;
    // If in edit mode — save the edit instead of sending a new message
    if (editingMsg) { await saveEdit(); return; }

    setSendNotice("");
    setSending(true);
    const draftText = text;

    // Optimistic append — avoids hard refresh flicker
    const optimisticId = `tmp-${Date.now()}`;
    const optimisticMsg = {
      id: optimisticId,
      conversation_id: selected.id,
      sender_type: "atendente",
      sender_user_id: user.id,
      message: draftText,
      media_url: "",
      status: "sending",
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setText("");
    setReplyTo(null);

    try {
      let savedId = null;
      if (sendMode === "whatsapp") {
        const liveChannel = await loadSendChannel();
        if (!liveChannel?.ready) {
          setSendNotice(liveChannel?.error || "WhatsApp indisponível para envio real.");
          // Roll back optimistic
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          setText(draftText);
          return;
        }
        const latestWindow = await api.get(`/conversations/${selected.id}/window`).then((response) => response.data).catch(() => null);
        if (latestWindow) setWindowStatus(latestWindow);
        if (latestWindow?.channel_mode === "cloud_api" && latestWindow?.requires_template) {
          setSendNotice("Essa conversa está fora da janela de 24h da Cloud API. Use um template aprovado para retomar o contato.");
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          setText(draftText);
          return;
        }
        const res = await api.post(`/conversations/${selected.id}/send-whatsapp`, { message: draftText });
        savedId = Number(res?.data?.id || 0);
      } else {
        const res = await api.post(`/conversations/${selected.id}/messages`, {
          sender_type: "atendente",
          message: draftText,
        });
        savedId = Number(res?.data?.id || 0);
      }

      // ── Confirmar mensagem optimista em-lugar — sem re-fetch, sem flicker ──
      if (savedId) {
        recentlySentIdsRef.current.add(savedId);
        // Limpar após 8s (tempo suficiente para o SSE chegar e ser ignorado)
        setTimeout(() => recentlySentIdsRef.current.delete(savedId), 8000);
        setMessages((prev) => prev.map((m) =>
          m.id === optimisticId
            ? { ...m, id: savedId, status: "sent", _optimistic: false }
            : m
        ));
      } else {
        // Fallback: remover optimista; SSE vai sincronizar
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      }

      // Mover conversa para o topo sem re-fetch completo
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === selected.id);
        if (idx < 0) return prev;
        const updated = { ...prev[idx], updated_at: new Date().toISOString() };
        return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });

      // Só atualiza janela de serviço (fetch pequeno, não causa re-render de lista)
      loadConversationWindow(selected).catch(() => {});
      // SSE vai sincronizar o restante (conversation_updated, summary_updated)
    } catch (err) {
      setSendNotice(err.response?.data?.error || "Erro ao enviar mensagem.");
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setText(draftText);
    } finally {
      setSending(false);
    }
  }

  async function sendTemplateMessage(e) {
    e.preventDefault();
    if (!selected || !templateDraft.template_id || sending) return;

    setSendNotice("");
    setSending(true);
    try {
      await api.post(`/conversations/${selected.id}/send-template`, {
        template_id: Number(templateDraft.template_id),
        variables: parseTemplateVariables(templateDraft.variables),
      });
      setTemplateDraft({ template_id: "", variables: "" });
      setSendNotice("Template enviado ao cliente.");
      await loadMessages(selected, { silent: true, refreshContact: false });
      loadConversations().catch(() => {});
      loadConversationWindow(selected).catch(() => {});
      setPanelRefreshToken((current) => current + 1);
    } catch (err) {
      setSendNotice(err.response?.data?.error || "Erro ao enviar template.");
    } finally {
      setSending(false);
    }
  }

  async function sendAudio() {
    if (!selected || !audioRecorder.blob) return;
    setSending(true);
    try {
      const formData = new FormData();
      const ext = audioRecorder.blob.type.includes("webm") ? "webm" : "ogg";
      formData.append("audio", audioRecorder.blob, `audio.${ext}`);
      await api.post(`/conversations/${selected.id}/send-audio`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      audioRecorder.reset();
      await loadMessages(selected, { silent: true, refreshContact: false });
    } catch (err) {
      setSendNotice(err.response?.data?.error || "Erro ao enviar áudio.");
    } finally {
      setSending(false);
    }
  }

  // ── Send file/image ─────────────────────────────────────────
  function handleAttachClick() {
    attachInputRef.current?.click();
  }

  function handleAttachChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    setAttachFile({ file, previewUrl, isImage });
    e.target.value = "";
  }

  function cancelAttach() {
    if (attachFile?.previewUrl) URL.revokeObjectURL(attachFile.previewUrl);
    setAttachFile(null);
  }

  async function sendAttachedFile() {
    if (!selected || !attachFile) return;
    setSendingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", attachFile.file, attachFile.file.name);
      await api.post(`/conversations/${selected.id}/send-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      cancelAttach();
      await loadMessages(selected, { silent: true, refreshContact: false });
    } catch (err) {
      setSendNotice(err.response?.data?.error || "Erro ao enviar arquivo.");
    } finally {
      setSendingFile(false);
    }
  }

  // ── Transfer conversation ─────────────────────────────────
  async function transferConversation() {
    if (!selected || !transferTarget) return;
    setTransferring(true);
    try {
      const assignee = users.find((u) => u.id === transferTarget);
      await api.patch(`/conversations/${selected.id}/assign`, { assigned_to: transferTarget });
      await loadConversations();
      await loadMessages({ ...selected, assigned_to: transferTarget, attendant_name: assignee?.name || "" });
      showToast({ variant: "success", title: "Conversa transferida", message: `Agora está com ${assignee?.name || "outro atendente"}.` });
      setTransferOpen(false);
      setTransferTarget(null);
    } catch (err) {
      showToast({ variant: "danger", title: "Erro", message: err.response?.data?.error || "Erro ao transferir." });
    } finally {
      setTransferring(false);
    }
  }

  async function loadAiSuggestions() {
    if (!selected) return;
    setAiOpen(true);
    setAiLoading(true);
    setAiData(null);
    try {
      const res = await api.post(`/conversations/${selected.id}/ai-suggest`);
      setAiData(res.data);
    } catch {
      setAiData({ suggestions: [], summary: "Não foi possível gerar sugestões no momento.", sentiment: "neutral", temperature: "frio" });
    } finally {
      setAiLoading(false);
    }
  }

  async function assignToMe() {
    if (!selected) return;
    try {
      await api.patch(`/conversations/${selected.id}/assign`, { assigned_to: user.id });
      await loadConversations();
      await loadMessages({ ...selected, assigned_to: user.id, attendant_name: user.name, status: "atendimento" });
      await loadSummary();
      showToast({ variant: "success", title: "Conversa assumida", message: `${selected.contact_name || "Cliente"} agora está com você.` });
    } catch (err) {
      showToast({ variant: "danger", title: "Erro", message: err?.response?.data?.error || "Não foi possível assumir a conversa." });
    }
  }

  async function changeStatus(status) {
    if (!selected) return;
    // Intercept "finalizado" to show Finalize modal first
    if (status === "finalizado") {
      setFinalizePending(selected);
      return;
    }
    try {
      await api.patch(`/conversations/${selected.id}/status`, { status });
      await loadConversations();
      await loadMessages({ ...selected, status });
      await loadSummary();
      showToast({
        variant: status === "atendimento" ? "info" : "success",
        title: status === "atendimento" ? "Atendimento iniciado" : "Status atualizado",
        message: `Conversa marcada como ${conversationStatusLabel(status).toLowerCase()}.`,
      });
    } catch (err) {
      showToast({ variant: "danger", title: "Erro", message: err?.response?.data?.error || "Não foi possível alterar o status." });
    }
  }

  async function archiveConversation() {
    if (!selected) return;
    setArchiving(true);
    try {
      await api.patch(`/conversations/${selected.id}/status`, { status: "arquivado" });
      await loadConversations();
      setArchiveOpen(false);
      showToast({ variant: "success", title: "Conversa arquivada", message: "O histórico pode ser consultado em Contatos." });
    } catch (err) {
      showToast({ variant: "danger", title: "Erro", message: err?.response?.data?.error || "Erro ao arquivar conversa." });
    } finally {
      setArchiving(false);
    }
  }

  async function returnToQueue() {
    if (!selected) return;
    try {
      await api.patch(`/conversations/${selected.id}/assign`, { assigned_to: "" });
      await api.patch(`/conversations/${selected.id}/status`, { status: "novo" });
      await loadConversations();
      await loadMessages({ ...selected, assigned_to: null, attendant_name: "", status: "novo" });
      await loadSummary();
      showToast({ variant: "info", title: "Conversa devolvida", message: "Voltou para a fila de atendimento." });
    } catch (err) {
      showToast({ variant: "danger", title: "Erro", message: err?.response?.data?.error || "Erro ao devolver para a fila." });
    }
  }

  async function finalizeConversation(conversation, result = {}) {
    await loadConversations();
    if (selectedRef.current && Number(selectedRef.current.id) === Number(conversation.id)) {
      await loadMessages({ ...conversation, status: "finalizado" });
    }
    await loadSummary();
    setFinalizePending(null);

    // Toast com resultado das ações automáticas
    const extras = [];
    if (result?.lead_created) extras.push(`Oportunidade criada no funil (${result.lead_created.stage})`);
    if (result?.nps_sent)     extras.push("NPS enviado ao cliente via WhatsApp ✓");
    if (result?.nps_error)    extras.push(`NPS não enviado: ${result.nps_error}`);

    showToast({
      variant: "success",
      title: "Atendimento finalizado",
      message: extras.length ? extras.join(" · ") : `Conversa de ${conversation.contact_name || "cliente"} encerrada.`,
    });
  }

  async function updateAssignment(assignedTo) {
    if (!selected) return;
    const assignee = users.find((item) => String(item.id) === String(assignedTo));
    await api.patch(`/conversations/${selected.id}/assign`, { assigned_to: assignedTo || "" });
    await loadConversations();
    await loadMessages({
      ...selected,
      assigned_to: assignedTo || null,
      attendant_name: assignee?.name || "",
      status: "atendimento",
    });
    await loadSummary();
    setSendNotice(assignedTo ? `Conversa atribuída para ${assignee?.name || "atendente"}.` : "Conversa ficou sem atendente.");
  }

  async function autoAssignConversation() {
    if (!selected || assigningAutomatically) return;
    setAssigningAutomatically(true);
    try {
      const response = await api.post(`/conversations/${selected.id}/auto-assign`, {
        strategy: autoAssignStrategy,
      });
      await loadConversations();
      await loadMessages({
        ...selected,
        assigned_to: response.data.assigned_to,
        attendant_name: response.data.attendant_name,
        status: "atendimento",
      });
      await loadSummary();
      setSendNotice(`Conversa distribuída automaticamente para ${response.data.attendant_name}.`);
    } catch (err) {
      setSendNotice(err.response?.data?.error || "Não foi possível distribuir a conversa automaticamente.");
    } finally {
      setAssigningAutomatically(false);
    }
  }

  async function markConversationRead(conversationId, silent = false) {
    if (!conversationId) return;
    try {
      await api.post(`/conversations/${conversationId}/read`);
      setConversations((current) => current.map((item) => (
        item.id === conversationId ? { ...item, unread_count: 0 } : item
      )));
      if (selectedRef.current && Number(selectedRef.current.id) === Number(conversationId)) {
        const nextSelected = { ...selectedRef.current, unread_count: 0 };
        selectedRef.current = nextSelected;
        setSelected(nextSelected);
      }
      if (!silent) setSendNotice("Conversa marcada como lida.");
    } catch {}
  }

  const [simulateModal, setSimulateModal] = useState(false);
  const [simulateForm, setSimulateForm] = useState({ name: "", phone: "", message: "" });

  async function simulateClientMessage(e) {
    e.preventDefault();
    if (!simulateForm.name || !simulateForm.phone || !simulateForm.message) return;
    try {
      await api.post("/dev/simulate-message", { ...simulateForm, phone: normalizeBrazilPhone(simulateForm.phone) });
      setSimulateModal(false);
      setSimulateForm({ name: "", phone: "", message: "" });
      await loadConversations(); await loadSummary();
    } catch (err) {
      setFetchError(err.response?.data?.error || "Não foi possível simular a mensagem.");
    }
  }

  function scheduleRealtimeRefresh(next) {
    const state = refreshStateRef.current;
    if (next.conversations) state.conversations = true;
    if (next.selected) state.selected = true;
    if (next.summary) state.summary = true;
    if (next.session) state.session = true;

    if (state.timer) return;
    state.timer = setTimeout(async () => {
      const pending = { ...state };
      refreshStateRef.current = { conversations: false, selected: false, summary: false, session: false };
      clearTimeout(pending.timer);

      if (pending.conversations) await loadConversations();
      if (pending.selected && selectedRef.current) await refreshSelectedConversation(selectedRef.current, { refreshContact: true });
      if (pending.summary) await loadSummary();
      if (pending.session) {
        await loadWhatsappSession();
        await loadSendChannel();
      }
    }, 250);
  }

  useEffect(() => {
    loadConversations(); loadUsers(); loadSummary(); loadWhatsappSession(); loadSendChannel(); loadTemplates(); loadQuickReplies();
  }, []);

  // Auto-open conversation when navigated from elsewhere (e.g. Live Panel "Ir para conversa")
  useEffect(() => {
    if (!initialConversationId) return;
    const target = conversations.find((c) => Number(c.id) === Number(initialConversationId));
    if (target) {
      loadMessages(target);
      clearInitialConversation?.();
    }
  }, [initialConversationId, conversations]); // eslint-disable-line

  // Separate normal (individual) conversations from group conversations
  const normalConversations = useMemo(() => conversations.filter((c) => !c.contact_is_group), [conversations]);
  const groupConversations  = useMemo(() => conversations.filter((c) => !!c.contact_is_group), [conversations]);

  const counts = useMemo(() => ({
    all: normalConversations.length,
    mine: normalConversations.filter((item) => String(item.assigned_to || "") === String(user.id)).length,
    unassigned: normalConversations.filter((item) => !item.assigned_to).length,
    open: normalConversations.filter((item) => item.status !== "finalizado").length,
    unread: normalConversations.filter((item) => Number(item.unread_count || 0) > 0).length,
    novo: normalConversations.filter((item) => item.status === "novo").length,
    atendimento: normalConversations.filter((item) => item.status === "atendimento").length,
    finalizado: normalConversations.filter((item) => item.status === "finalizado").length,
    groups: groupConversations.length,
    groupsUnread: groupConversations.filter((item) => Number(item.unread_count || 0) > 0).length,
  }), [normalConversations, groupConversations, user.id]);

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    // Groups tab: only show group conversations, sorted by most recent
    if (typeFilter === "groups") {
      return groupConversations.filter((conversation) => {
        if (!term) return true;
        const haystack = [conversation.contact_name, conversation.last_message].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(term);
      }).map((conversation) => ({
        ...conversation,
        slaMeta: getSlaMeta(conversation),
        priorityMeta: getConversationPriority(conversation, user.id),
      })).sort((left, right) => {
        const rightUpdated = new Date(right.updated_at || right.created_at || 0).getTime();
        const leftUpdated  = new Date(left.updated_at  || left.created_at  || 0).getTime();
        return rightUpdated - leftUpdated;
      });
    }
    // Normal tab: only show individual (non-group) conversations
    return normalConversations.filter((conversation) => {
      if (ownerFilter === "mine" && String(conversation.assigned_to || "") !== String(user.id)) return false;
      if (ownerFilter === "unassigned" && conversation.assigned_to) return false;
      if (statusFilter === "open" && conversation.status === "finalizado") return false;
      if (statusFilter === "unread" && Number(conversation.unread_count || 0) === 0) return false;
      if (!["all", "open", "unread"].includes(statusFilter) && conversation.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [
        conversation.contact_name,
        conversation.contact_phone,
        conversation.last_message,
        conversation.attendant_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    }).map((conversation) => ({
      ...conversation,
      slaMeta: getSlaMeta(conversation),
      priorityMeta: getConversationPriority(conversation, user.id),
    })).sort((left, right) => {
      const rightUpdated = new Date(right.updated_at || right.created_at || 0).getTime();
      const leftUpdated  = new Date(left.updated_at  || left.created_at  || 0).getTime();
      // Modo urgente: prioridade primeiro, depois recência
      if (sortOrder === "priority") {
        if (right.priorityMeta.score !== left.priorityMeta.score) return right.priorityMeta.score - left.priorityMeta.score;
      }
      // Modo recente (padrão): sempre mais recente primeiro
      return rightUpdated - leftUpdated;
    });
  }, [normalConversations, groupConversations, ownerFilter, search, statusFilter, typeFilter, sortOrder, user.id]);

  // Auto-open: always keep the first conversation open when enabled and nothing is selected
  useEffect(() => {
    if (!autoOpenFirst) return;
    if (selected) return;
    if (filteredConversations.length === 0) return;
    loadMessages(filteredConversations[0]);
  }, [filteredConversations, autoOpenFirst]); // eslint-disable-line

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      // SSE cobre atualizações em tempo real; polling é só fallback de resiliência
      loadConversations();
      loadWhatsappSession();
      loadSendChannel();
    };
    const interval = setInterval(tick, 30000); // 5s → 30s: SSE cobre tempo real
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!selected) return;
    const tickMessages = () => {
      if (document.visibilityState === "hidden") return;
      api.get(`/conversations/${selected.id}/messages`, { params: { limit: 50 } })
        .then((response) => {
          const next = ensureArray(response.data?.messages || response.data);
          setMessages((prev) => {
            if (!next.length) return prev;
            // Evitar re-render desnecessário: pular se nenhuma mensagem mudou
            const lastPrev = prev[prev.length - 1];
            const lastNext = next[next.length - 1];
            if (prev.length === next.length && lastPrev?.id === lastNext?.id) return prev;
            return next;
          });
        })
        .catch(() => {});
    };
    const interval = setInterval(tickMessages, 30000); // 15s → 30s com dedup
    return () => clearInterval(interval);
  }, [selected?.id]);

  useEffect(() => {
    selectedRef.current = selected;
    onActiveConversationChange?.(selected?.id || null);
  }, [onActiveConversationChange, selected]);

  // Mobile: auto-navigate to chat panel when a conversation is selected
  useEffect(() => {
    if (selected) setMobilePanel("chat");
  }, [selected?.id]); // eslint-disable-line

  // Close more-menu on click outside
  useEffect(() => {
    if (!moreMenuOpen) return;
    function handler(e) {
      const inMenu = moreMenuRef.current && moreMenuRef.current.contains(e.target);
      const inBtn  = moreBtnRef.current  && moreBtnRef.current.contains(e.target);
      if (!inMenu && !inBtn) setMoreMenuOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [moreMenuOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 50);
    else setSearchQuery("");
  }, [searchOpen]);

  // Auto-scroll to bottom when conversation changes (always) or new message arrives (unless loading older)
  useEffect(() => {
    if (skipScrollRef.current) return;
    const prevCount = prevMsgCountRef.current;
    const newCount  = messages.length;
    prevMsgCountRef.current = newCount;

    // Conversation changed → always jump to bottom, reset counters
    if (!prevCount || selected?.id !== prevConvIdRef.current) {
      setIsAtBottom(true);
      setScrollUnread(0);
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      prevConvIdRef.current = selected?.id;
      return;
    }
    prevConvIdRef.current = selected?.id;

    if (newCount > prevCount) {
      if (isAtBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        // New messages arrived while user scrolled up — increment badge
        setScrollUnread((n) => n + (newCount - prevCount));
      }
    }
  // eslint-disable-next-line
  }, [messages.length, selected?.id]);

  useEffect(() => {
    let cancelled = false;

    async function connectRealtime() {
      try {
        const tokenResponse = await api.post("/realtime/token");
        if (cancelled) return;

        const source = new EventSource(`${API}/realtime/stream?token=${encodeURIComponent(tokenResponse.data.token)}`);
        streamRef.current = source;

        const onSession = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            const next = ensureObject(payload.session);
            if (next) {
              setSessionStatus(next);
              loadSendChannel();
            }
          } catch {}
        };

        const onSummary = () => scheduleRealtimeRefresh({ conversations: true, summary: true });

        const onConversation = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            const shouldRefreshSelected = selectedRef.current && Number(payload.conversationId) === Number(selectedRef.current.id);
            if (shouldRefreshSelected) {
              setPanelRefreshToken((current) => current + 1);
            }
            scheduleRealtimeRefresh({
              conversations: true,
              selected: shouldRefreshSelected,
              summary: payload.reason === "status" || payload.reason === "assignment",
            });
          } catch {}
        };

        const onMessage = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            const shouldRefreshSelected = selectedRef.current && Number(payload.conversationId) === Number(selectedRef.current.id);
            if (payload.senderType === "cliente" && shouldRefreshSelected) {
              markConversationRead(payload.conversationId, true);
              // Client sent a message → they're online right now
              setContactPresence({ is_online: true, last_seen_at: new Date().toISOString() });
            }
            if (shouldRefreshSelected) {
              setPanelRefreshToken((current) => current + 1);
            }
            // Se a mensagem foi enviada pelo próprio usuário e já foi confirmada
            // em-lugar pelo sendMessage, não re-buscar o painel de mensagens
            const isSelfConfirmed = payload.messageId
              && recentlySentIdsRef.current.has(Number(payload.messageId));
            scheduleRealtimeRefresh({
              conversations: true,
              selected: shouldRefreshSelected && !isSelfConfirmed,
              summary: !isSelfConfirmed,
            });
          } catch {}
        };

        const onTimeline = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            if (selectedRef.current && Number(payload.conversationId) === Number(selectedRef.current.id)) {
              setPanelRefreshToken((current) => current + 1);
            }
          } catch {}
        };

        // ── Context menu realtime events ──────────────────────────
        const onMsgUpdated = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            if (selectedRef.current && Number(payload.conversationId) === Number(selectedRef.current.id)) {
              const updated = payload.message;
              if (updated) {
                setMessages((prev) => prev.map((m) => m.id === updated.id ? { ...m, ...updated } : m));
              }
            }
          } catch {}
        };
        const onMsgPinned = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            if (selectedRef.current && Number(payload.conversationId) === Number(selectedRef.current.id)) {
              setMessages((prev) => prev.map((m) => m.id === Number(payload.messageId) ? { ...m, is_pinned: payload.is_pinned } : m));
            }
          } catch {}
        };
        const onMsgReacted = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            if (selectedRef.current && Number(payload.conversationId) === Number(selectedRef.current.id)) {
              setMessages((prev) => prev.map((m) => m.id === Number(payload.messageId) ? { ...m, reactions_json: JSON.stringify(payload.reactions) } : m));
            }
          } catch {}
        };
        const onMsgDeleted = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            if (selectedRef.current && Number(payload.conversationId) === Number(selectedRef.current.id)) {
              // Soft-delete: mark message as deleted, don't remove it from list
              setMessages((prev) => prev.map((m) =>
                m.id === Number(payload.messageId) ? { ...m, is_deleted: 1 } : m
              ));
              // Also update presence since this indicates activity
              setContactPresence((p) => p ? { ...p, last_seen_at: new Date().toISOString() } : p);
            }
          } catch {}
        };

        const onContactTyping = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            if (!selectedRef.current) return;
            if (Number(payload.conversationId) !== Number(selectedRef.current.id)) return;
            setIsContactTyping(!!payload.isTyping);
            // Auto-clear after 8s in case "paused" event never arrives
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
            if (payload.isTyping) {
              typingTimerRef.current = setTimeout(() => setIsContactTyping(false), 8000);
            }
          } catch {}
        };


        source.addEventListener("session_updated", onSession);
        source.addEventListener("summary_updated", onSummary);
        source.addEventListener("conversation_updated", onConversation);
        source.addEventListener("message_created", onMessage);
        source.addEventListener("message_status", onMessage);
        source.addEventListener("timeline_updated", onTimeline);
        source.addEventListener("message_updated", onMsgUpdated);
        source.addEventListener("message_pinned", onMsgPinned);
        source.addEventListener("message_reacted", onMsgReacted);
        source.addEventListener("message_deleted", onMsgDeleted);
        source.addEventListener("contact_typing", onContactTyping);

        source.onerror = () => {
          source.close();
          if (cancelled) return;
          reconnectRef.current = setTimeout(connectRealtime, 2500);
        };
      } catch {
        if (cancelled) return;
        reconnectRef.current = setTimeout(connectRealtime, 2500);
      }
    }

    connectRealtime();

    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (streamRef.current) streamRef.current.close();
      if (refreshStateRef.current.timer) clearTimeout(refreshStateRef.current.timer);
    };
  }, [api]);

  const queueInsight = useMemo(() => ({
    critical: filteredConversations.filter((item) => item.priorityMeta.level === "critical").length,
    warning: filteredConversations.filter((item) => item.priorityMeta.level === "warning").length,
    unassigned: filteredConversations.filter((item) => !item.assigned_to && item.status !== "finalizado").length,
  }), [filteredConversations]);

  const activeAttendants = useMemo(() => (
    users.filter((item) => item.active === 1 && item.role === "atendente").map((item) => ({
      ...item,
      openLoad: conversations.filter((conversation) => Number(conversation.assigned_to || 0) === Number(item.id) && conversation.status !== "finalizado").length,
    }))
  ), [conversations, users]);
  const activeAssignableUsers = useMemo(() => users.filter((item) => item.active === 1), [users]);

  const selectedVisible = selected ? filteredConversations.some((item) => item.id === selected.id) : false;
  const sessionTone = sessionStatus.status === "connected"
    ? "success"
    : (["qr_ready", "pairing_code_ready"].includes(sessionStatus.status) ? "warning" : "muted");
  const sendModeLabel = sendMode === "whatsapp"
    ? "A mensagem vai para o WhatsApp do cliente e entra no histórico."
    : "A mensagem fica só no CRM. Não será enviada ao cliente.";
  const whatsappSendDisabled = sendMode === "whatsapp" && (!sendChannel?.ready || sessionStatus?.status !== "connected");
  const windowTone = windowStatusTone(windowStatus);
  const templateWarningVisible = sendMode === "whatsapp" && windowStatus?.channel_mode === "cloud_api" && windowStatus?.requires_template;
  const selectedTemplate = templates.find((item) => String(item.id) === String(templateDraft.template_id));
  const selectedTemplateVariables = parseTemplateVariables(templateDraft.variables);
  const selectedSla = selected ? getSlaMeta(selected) : null;
  const quickReplyCategories = useMemo(() => ["all", ...Array.from(new Set(quickReplies.map((item) => item.category).filter(Boolean)))], [quickReplies]);
  const quickReplyContext = useMemo(() => ({
    name: selectedContact?.name || selected?.contact_name || "Cliente",
    company: selectedContact?.company || "",
    phone: formatBrazilPhone(selectedContact?.phone || selected?.contact_phone || "") || selectedContact?.phone || selected?.contact_phone || "",
    agent: user.name || "Atendimento",
  }), [selectedContact, selected, user.name]);
  const filteredQuickReplies = useMemo(() => {
    const term = quickReplySearch.trim().toLowerCase();
    return quickReplies.filter((item) => {
      if (quickReplyCategory !== "all" && item.category !== quickReplyCategory) return false;
      if (!term) return true;
      const haystack = [item.title, item.shortcut, item.category, item.body_text].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [quickReplies, quickReplyCategory, quickReplySearch]);
  const favoriteQuickReplies = useMemo(
    () => [...quickReplies]
      .filter((item) => item.is_favorite)
      .sort((a, b) => Number(b.usage_count || 0) - Number(a.usage_count || 0))
      .slice(0, 4),
    [quickReplies]
  );
  const suggestedQuickReplies = useMemo(() => {
    const categoryByStatus = {
      novo: ["atendimento", "proposta"],
      atendimento: ["atendimento", "suporte", "proposta"],
      finalizado: ["pos-venda", "cobranca"],
    };
    const desiredCategories = new Set(categoryByStatus[selected?.status] || ["atendimento"]);
    if (selectedSla?.tone === "critical" || selectedSla?.tone === "warning") desiredCategories.add("cobranca");
    const ranked = [...quickReplies]
      .filter((item) => desiredCategories.has(item.category) || item.is_favorite)
      .sort((a, b) => {
        if (Boolean(b.is_favorite) !== Boolean(a.is_favorite)) return Number(Boolean(b.is_favorite)) - Number(Boolean(a.is_favorite));
        return Number(b.usage_count || 0) - Number(a.usage_count || 0);
      });
    return ranked.slice(0, 4);
  }, [quickReplies, selected?.status, selectedSla?.tone]);
  const quickReplyCommand = useMemo(() => extractQuickReplyCommand(text), [text]);
  const quickReplyShortcutMatches = useMemo(() => {
    if (!quickReplyCommand) return [];
    return [...quickReplies]
      .filter((item) => item.shortcut && String(item.shortcut).toLowerCase().startsWith(quickReplyCommand))
      .sort((a, b) => {
        const aShortcut = String(a.shortcut || "").toLowerCase();
        const bShortcut = String(b.shortcut || "").toLowerCase();
        const exactA = aShortcut === quickReplyCommand;
        const exactB = bShortcut === quickReplyCommand;
        if (exactA !== exactB) return Number(exactB) - Number(exactA);
        if (Boolean(b.is_favorite) !== Boolean(a.is_favorite)) return Number(Boolean(b.is_favorite)) - Number(Boolean(a.is_favorite));
        return Number(b.usage_count || 0) - Number(a.usage_count || 0);
      })
      .slice(0, 6);
  }, [quickReplies, quickReplyCommand]);
  const shortcutPreviewReply = quickReplyShortcutMatches[quickReplyHighlightIndex] || quickReplyShortcutMatches[0] || null;

  useEffect(() => {
    setQuickReplyHighlightIndex(0);
  }, [quickReplyCommand, quickReplySearch, quickReplyCategory]);

  async function applyQuickReply(item, options = {}) {
    const { replaceCommand = false } = options;
    const rendered = renderQuickReplyBody(item.body_text, quickReplyContext).trim();
    if (!rendered) return;
    setText((current) => {
      if (replaceCommand) return replaceQuickReplyCommand(current, rendered);
      if (!current.trim()) return rendered;
      return `${current.trim()}\n\n${rendered}`;
    });
    setQuickReplyOpen(false);
    setSendNotice(`Resposta rápida "${item.title}" inserida no campo.`);
    try {
      await api.post(`/quick-replies/${item.id}/use`, {
        conversation_id: selected?.id || null,
      });
      await loadQuickReplies();
      setPanelRefreshToken((current) => current + 1);
    } catch {}
  }

  function handleComposerKeyDown(event) {
    // Quick-reply shortcut navigation (takes priority)
    if (quickReplyShortcutMatches.length) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setQuickReplyHighlightIndex((current) => (current + 1) % quickReplyShortcutMatches.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setQuickReplyHighlightIndex((current) => (current - 1 + quickReplyShortcutMatches.length) % quickReplyShortcutMatches.length);
        return;
      }
      if ((event.key === "Tab" || event.key === "Enter") && shortcutPreviewReply) {
        event.preventDefault();
        applyQuickReply(shortcutPreviewReply, { replaceCommand: true });
        return;
      }
      if (event.key === "Escape") {
        setQuickReplyHighlightIndex(0);
        return;
      }
    }

    // Ctrl/Cmd + formatting shortcuts
    if (event.ctrlKey || event.metaKey) {
      if (event.key === "b") { event.preventDefault(); applyFormat("bold"); return; }
      if (event.key === "i") { event.preventDefault(); applyFormat("italic"); return; }
      if (event.key === "e") { event.preventDefault(); applyFormat("code"); return; }
    }

    // Enter to send (when no quick-reply match is active)
    if (event.key === "Enter" && !event.shiftKey) {
      const enterToSend = localStorage.getItem("safezap_enter_to_send") !== "0";
      if (enterToSend && text.trim() && !sending) {
        event.preventDefault();
        sendMessage(event);
      }
    }
  }

  function applyFormat(type) {
    const ta = composerTextareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    const selected = text.slice(start, end);
    const delimiters = {
      bold:   ["*", "*"],
      italic: ["_", "_"],
      strike: ["~", "~"],
      code:   ["`", "`"],
      quote:  ["\n> ", ""],
    };
    const [open, close] = delimiters[type] || ["", ""];

    // Toggle: if already wrapped, unwrap; otherwise wrap
    const before = text.slice(0, start);
    const after  = text.slice(end);

    let newText, newStart, newEnd;
    if (
      type !== "quote" &&
      before.endsWith(open) && after.startsWith(close || open)
    ) {
      // Unwrap
      newText  = before.slice(0, -open.length) + selected + after.slice((close || open).length);
      newStart = start - open.length;
      newEnd   = end   - open.length;
    } else {
      newText  = before + open + selected + (close || open) + after;
      newStart = start + open.length;
      newEnd   = end   + open.length;
    }

    setText(newText);
    // Restore selection after React re-render
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newStart, newEnd);
    });
  }

  function handleTextareaSelect() {
    const ta = composerTextareaRef.current;
    if (!ta) return;
    setFmtBarVisible(ta.selectionStart !== ta.selectionEnd);
  }

  async function handleConversationCardAction(event, conversation, action) {
    event.preventDefault();
    event.stopPropagation();
    try {
      if (action === "open") {
        await loadMessages(conversation);
        return;
      }
      if (action === "assume") {
        await api.patch(`/conversations/${conversation.id}/assign`, { assigned_to: user.id });
        await loadConversations();
        await loadSummary();
        if (selectedRef.current && Number(selectedRef.current.id) === Number(conversation.id)) {
          await loadMessages({ ...conversation, assigned_to: user.id, attendant_name: user.name });
        }
        setSendNotice(`Conversa de ${conversation.contact_name} assumida por você.`);
        return;
      }
      if (action === "start") {
        await api.patch(`/conversations/${conversation.id}/status`, { status: "atendimento" });
        await loadConversations();
        await loadSummary();
        if (selectedRef.current && Number(selectedRef.current.id) === Number(conversation.id)) {
          await loadMessages({ ...conversation, status: "atendimento" });
        }
        setSendNotice(`Conversa de ${conversation.contact_name} colocada em atendimento.`);
        return;
      }
      if (action === "finish") {
        setFinalizePending(conversation);
        return;
      }
    } catch (err) {
      setSendNotice(err?.response?.data?.error || "Não foi possível atualizar a conversa agora.");
    }
  }

  return (
    <>
      {/* ── 3-dot dropdown (fixed, escapes overflow:hidden parents) ── */}
      {moreMenuOpen && (
        <div
          ref={moreMenuRef}
          className="chatMoreMenu"
          style={{ position: "fixed", top: moreMenuPos.top, right: moreMenuPos.right, zIndex: 2000 }}
        >
          <button
            type="button"
            className="chatMoreMenuItem"
            onClick={() => { setMoreMenuOpen(false); setSearchOpen((v) => !v); }}
          >
            <Icon name="search" size={13} /> Pesquisar na conversa
          </button>
          <button
            type="button"
            className="chatMoreMenuItem"
            onClick={() => { setMoreMenuOpen(false); setArchiveOpen(true); }}
          >
            <Icon name="download" size={13} /> Arquivar conversa
          </button>
        </div>
      )}

      {/* Finalize modal — shown when clicking "Finalizar" */}
      {finalizePending && (
        <FinalizeModal
          conversation={finalizePending}
          api={api}
          onConfirm={(result) => finalizeConversation(finalizePending, result)}
          onBack={() => setFinalizePending(null)}
        />
      )}

      {fetchError && <div className="errorBanner">{fetchError}</div>}

      {/* Archive conversation modal */}
      {archiveOpen && (
        <div className="modal-overlay-v2" onClick={() => !archiving && setArchiveOpen(false)}>
          <div className="modal-v2 modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Arquivar conversa?</h3>
            </div>
            <div className="modal-body">
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                background: "var(--bg-2)", border: "1px solid var(--border)",
                borderRadius: "var(--r-4)", padding: "12px 14px",
              }}>
                <Icon name="info" size={16} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
                  A conversa sairá da lista ativa, mas o histórico ficará preservado e poderá ser consultado em Contatos.
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setArchiveOpen(false)}
                disabled={archiving}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={archiveConversation}
                disabled={archiving}
              >
                {archiving ? "Arquivando…" : "Arquivar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer conversation modal */}
      {transferOpen && (
        <div className="modal-overlay-v2">
          <div className="modal-v2 modal-md">
            <div className="modal-header">
              <h3>Transferir conversa</h3>
              <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => { setTransferOpen(false); setTransferTarget(null); }}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-2)", marginBottom: 12 }}>
                Selecione o atendente que vai assumir esta conversa:
              </p>
              <div className="agent-list">
                {users.filter((u) => u.active === 1 && u.id !== selected?.assigned_to).map((u) => (
                  <div
                    key={u.id}
                    className={`agent-option${transferTarget === u.id ? " selected" : ""}`}
                    onClick={() => setTransferTarget(u.id)}
                  >
                    <Avatar name={u.name} size="sm" />
                    <div>
                      <strong>{u.name}</strong>
                      <span>{u.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary btn-sm" onClick={transferConversation} disabled={!transferTarget || transferring}>
                {transferring ? "Transferindo…" : "Transferir"}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setTransferOpen(false); setTransferTarget(null); }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {simulateModal && (
        <div className="modal-overlay-v2">
          <div className="modal-v2 modal-sm">
            <div className="modal-header">
              <h3>Iniciar novo contato</h3>
              <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => setSimulateModal(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <form onSubmit={simulateClientMessage}>
              <div className="modal-body">
                <div className="field" style={{ marginBottom: 12 }}>
                  <label className="field-label-v2">Nome do cliente</label>
                  <input value={simulateForm.name} onChange={(e) => setSimulateForm((f) => ({ ...f, name: e.target.value }))} placeholder="Informe o nome" />
                </div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <label className="field-label-v2">Telefone</label>
                  <input value={simulateForm.phone} onChange={(e) => setSimulateForm((f) => ({ ...f, phone: maskBrazilPhoneInput(e.target.value) }))} placeholder="(83) 99999-9999" />
                </div>
                <div className="field">
                  <label className="field-label-v2">Mensagem</label>
                  <input value={simulateForm.message} onChange={(e) => setSimulateForm((f) => ({ ...f, message: e.target.value }))} placeholder="Informe a mensagem" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary btn-sm">Iniciar</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSimulateModal(false)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Stats bar e Distribuição inteligente removidos — não existem no design v2.0.
          Os dados de métricas pertencem ao Dashboard (Resumo). */}
      <section className="workspace attendanceWorkspace">
        <div className={`conversationList${mobilePanel !== "list" ? " mobileHidden" : ""}`}>
            <div className="panelHeader attendanceListHeader">
              <div>
                <h3>Fila de atendimento</h3>
                <p>{counts.open} conversas · {activeAttendants.length} atendentes</p>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  className={`tb-icon-btn${autoOpenFirst ? " active" : ""}`}
                  title={autoOpenFirst ? "Auto-abrir: ativado" : "Auto-abrir primeiro da lista"}
                  onClick={() => setAutoOpenFirst((prev) => {
                    const next = !prev;
                    localStorage.setItem("safezap_auto_open_first", next ? "1" : "0");
                    return next;
                  })}
                  style={autoOpenFirst ? { color: "var(--brand-500)", background: "var(--brand-glow)" } : undefined}
                >
                  <Icon name="zap" size={13} />
                </button>
                <button
                  className={`tb-icon-btn sort-toggle-btn${sortOrder === "priority" ? " active" : ""}`}
                  title={sortOrder === "recent" ? "Ordenando por: Mais recente — clique para ordenar por Urgência" : "Ordenando por: Urgência — clique para ordenar por Mais recente"}
                  onClick={() => setSortOrder((prev) => {
                    const next = prev === "recent" ? "priority" : "recent";
                    localStorage.setItem("safezap_sort_order", next);
                    return next;
                  })}
                >
                  {sortOrder === "priority" ? <Icon name="zap" size={13} /> : <Icon name="sort" size={13} />}
                </button>
                <button className="tb-icon-btn" title="Filtrar"><Icon name="filter" size={13} /></button>
              </div>
            </div>

          <div className="attendanceToolbar">
            <div className="conversationSearchBox">
              <span><Icon name="search" /></span>
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); if (msgSearchResults !== null) setMsgSearchResults(null); }}
                onKeyDown={(e) => { if (e.key === "Enter" && search.trim().length >= 2) searchMessages(search); }}
                placeholder="Buscar conversa ou ↵ buscar em mensagens"
              />
              {search.length >= 2 && (
                <button
                  className="tb-icon-btn"
                  style={{ flexShrink: 0, fontSize: 10, color: "var(--text-3)", padding: "0 6px" }}
                  title="Buscar em mensagens (FTS)"
                  onClick={() => searchMessages(search)}
                >
                  {msgSearchLoading ? "…" : "em msg"}
                </button>
              )}
              {msgSearchResults !== null && (
                <button className="tb-icon-btn" title="Limpar busca" onClick={() => { setMsgSearchResults(null); setSearch(""); }}>
                  <Icon name="close" size={11} />
                </button>
              )}
            </div>

            <div className="segment" style={{ flexWrap: "wrap" }}>
              <button type="button" className={`segment-item${typeFilter === "normal" && ownerFilter === "all" && statusFilter === "open" ? " active" : ""}`} onClick={() => { setTypeFilter("normal"); setOwnerFilter("all"); setStatusFilter("open"); }}>
                Todas <span className="count">{counts.all}</span>
              </button>
              <button type="button" className={`segment-item${typeFilter === "normal" && ownerFilter === "mine" ? " active" : ""}`} onClick={() => { setTypeFilter("normal"); setOwnerFilter("mine"); setStatusFilter("open"); }}>
                Minhas <span className="count">{counts.mine}</span>
              </button>
              <button type="button" className={`segment-item${typeFilter === "normal" && ownerFilter === "unassigned" ? " active" : ""}`} onClick={() => { setTypeFilter("normal"); setOwnerFilter("unassigned"); setStatusFilter("open"); }}>
                Pendentes <span className="count">{counts.unassigned}</span>
              </button>
              <button type="button" className={`segment-item${typeFilter === "normal" && statusFilter === "novo" ? " active" : ""}`} onClick={() => { setTypeFilter("normal"); setOwnerFilter("all"); setStatusFilter("novo"); }}>
                Novas <span className="count">{counts.novo}</span>
              </button>
              <button type="button" className={`segment-item${typeFilter === "normal" && statusFilter === "finalizado" ? " active" : ""}`} onClick={() => { setTypeFilter("normal"); setOwnerFilter("all"); setStatusFilter("finalizado"); }}>
                Finalizadas <span className="count">{counts.finalizado}</span>
              </button>
              {counts.groups > 0 && (
                <button type="button" className={`segment-item segment-item-groups${typeFilter === "groups" ? " active" : ""}`} onClick={() => setTypeFilter("groups")}>
                  <Icon name="whatsapp" size={11} /> Grupos
                  {counts.groupsUnread > 0 ? <span className="count count-brand">{counts.groupsUnread}</span> : <span className="count">{counts.groups}</span>}
                </button>
              )}
            </div>
          </div>

          {sortOrder === "priority" && (
            <div className="sortOrderNotice">
              <Icon name="zap" size={10} />
              Ordenando por urgência — <button onClick={() => { setSortOrder("recent"); localStorage.setItem("safezap_sort_order", "recent"); }}>voltar ao mais recente</button>
            </div>
          )}

          <div className="conversationItems">
          {selected && !selectedVisible && (
            <div className="listFilterNotice">
              A conversa aberta no centro ficou fora dos filtros atuais.
              <button onClick={() => { setTypeFilter("normal"); setOwnerFilter("all"); setStatusFilter("open"); setSearch(""); }}>Limpar filtros</button>
            </div>
          )}

          {/* ── Cross-conversation message search results ── */}
          {msgSearchResults !== null ? (
            <>
              <div style={{ padding: "6px 12px 2px", fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>
                {msgSearchLoading ? "Buscando…" : `${msgSearchResults.length} resultado${msgSearchResults.length !== 1 ? "s" : ""} em mensagens`}
              </div>
              {msgSearchResults.length === 0 && !msgSearchLoading && (
                <div className="emptyState">Nenhuma mensagem encontrada para "{search}".</div>
              )}
              {msgSearchResults.map((r) => (
                <div
                  key={`msg-${r.message_id}`}
                  className="conversationItem"
                  onClick={async () => {
                    const conv = conversations.find((c) => c.id === r.conversation_id);
                    if (conv) { loadMessages(conv); }
                    else {
                      // Conversation not in loaded page — load it directly
                      try {
                        const res = await api.get(`/conversations/${r.conversation_id}/messages`);
                        const fakeConv = { id: r.conversation_id, contact_name: r.contact_name, contact_phone: r.contact_phone, contact_avatar_url: r.contact_avatar };
                        setSelected(fakeConv);
                        setMessages(ensureArray(res.data));
                      } catch {}
                    }
                    setMsgSearchResults(null);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Avatar name={r.contact_name} size="md" src={r.contact_avatar} />
                  <div className="conversationText">
                    <div className="conversationTitleRow">
                      <strong>{r.contact_name}</strong>
                      <span style={{ fontSize: 10, color: "var(--text-4)", flexShrink: 0, marginLeft: 6 }}>
                        {formatTime(r.created_at)}
                      </span>
                    </div>
                    <span style={{ color: "var(--brand-500)", fontSize: 11 }}>
                      {r.sender_type === "atendente" ? "Você: " : ""}
                      {r.message?.slice(0, 100)}
                    </span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {filteredConversations.length === 0 && (
                <div className="emptyState">
                  {conversations.length === 0
                    ? <>Nenhuma conversa ainda.<br />Clique em "Simular mensagem".</>
                    : <>Nenhuma conversa encontrada com os filtros atuais.<br />Ajuste a busca ou mude os filtros.</>}
                </div>
              )}

              {filteredConversations.map((conversation) => {
                const unread = Number(conversation.unread_count || 0);
                const attendantName = conversation.attendant_name || "";
                const waitBadge = calcWaitBadge(conversation);
                const isGroupConv = !!conversation.contact_is_group;
                const stateClass = getConvStateClass(conversation);
                const relTime = formatRelativeTime(conversation.updated_at || conversation.created_at);
                const preview = formatMediaPreview(conversation.last_message);
                // Tooltip do timestamp completo (acessibilidade)
                const fullTime = conversation.updated_at
                  ? new Date(conversation.updated_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                  : "";
                return (
                  <div
                    key={conversation.id}
                    className={`conversationItem ${selected?.id === conversation.id ? "selected" : ""} ${unread > 0 ? "unread" : ""} ${stateClass}`}
                    onClick={() => loadMessages(conversation)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); loadMessages(conversation); } }}
                    role="button"
                    tabIndex={0}
                  >
                    {/* Avatar com mini-badge de atendente */}
                    <div className="ciAvatarWrap">
                      {isGroupConv ? (
                        <div className="ciGroupAvatar" title={conversation.contact_name}>
                          <Icon name="whatsapp" size={18} />
                        </div>
                      ) : (
                        <Avatar name={conversation.contact_name} size="md" src={conversation.contact_avatar_url} />
                      )}
                      {!isGroupConv && (attendantName ? (
                        <div
                          className="ciAttendantBadge assigned"
                          style={{ background: getAvatarColor(attendantName) }}
                          title={`Atendendo: ${attendantName}`}
                        >
                          {getInitials(attendantName)}
                        </div>
                      ) : (
                        <div className="ciAttendantBadge unassigned" title="Sem atendente">—</div>
                      ))}
                    </div>

                    <div className="conversationText">
                      {/* Linha 1: nome + timestamp relativo */}
                      <div className="conversationTitleRow">
                        <strong className="ciContactName">{conversation.contact_name}</strong>
                        <span className="ciTimestamp" title={fullTime}>{relTime}</span>
                      </div>

                      {/* Linha 2: preview de última mensagem */}
                      <span className={`ciPreviewText${preview.startsWith("🎤") || preview.startsWith("📷") || preview.startsWith("📄") || preview.startsWith("🎥") || preview.startsWith("📍") || preview.startsWith("💬") || preview.startsWith("🖼") || preview.startsWith("👤") ? " ciMediaPreview" : ""}`}>
                        {preview}
                      </span>

                      {/* Linha 3: badges de estado + contador de não-lidos */}
                      <div className="ciTagRow">
                        {waitBadge && (
                          <span className={`ciWaitBadge ${waitBadge.level}`}>
                            ⏱ {waitBadge.label}
                          </span>
                        )}
                        {conversation.status === "novo" && <span className="ciStateBadge badge-novo">Novo</span>}
                        {(conversation.tags || []).includes("VIP") && <span className="ciStateBadge badge-vip">VIP</span>}
                        {conversation.sector && (
                          <span className="ciStateBadge badge-sector">{conversation.sector}</span>
                        )}
                        {unread > 0 && (
                          <span className="ciUnreadCount">{unread > 99 ? "99+" : unread}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {hasMore && (
                <div style={{ padding: "10px 12px" }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ width: "100%", justifyContent: "center" }}
                    onClick={() => loadConversations(true)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Carregando…" : "Carregar mais conversas"}
                  </button>
                </div>
              )}
            </>
          )}
          </div>{/* end .conversationItems */}
        </div>

        <div className={`chatPanel${mobilePanel === "list" ? " mobileHidden" : ""}${mobilePanel === "crm" ? " mobileBehind" : ""}`}>
          {!selected ? (
            <div className="chatEmpty">
              <div className="chatEmptyIcon"><Icon name="chat" size={40} /></div>
              <h3>Selecione uma conversa</h3>
              <p>As mensagens e o contexto do cliente aparecem aqui em tempo real.</p>
            </div>
          ) : (
            <>
              <div className="chatHeader attendanceChatHeader">
                <button className="mobileBackBtn" onClick={() => setMobilePanel("list")} aria-label="Voltar">
                  <Icon name="chevronRight" size={18} />
                </button>
                <div className="chatIdentity">
                  <Avatar name={selected.contact_name} className="photo" size="md" src={selectedContact?.avatar_url || selected.contact_avatar_url} />
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                    <h3 style={{ margin: 0, lineHeight: 1.3 }}>{selected.contact_name}</h3>
                    <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                      <span>{formatBrazilPhone(selected.contact_phone || "") || selected.contact_phone || "Telefone não informado"}</span>
                      {/* ── Presence indicator ── */}
                      {contactPresence && (
                        <>
                          <span style={{ color: "var(--text-5)" }}>·</span>
                          {contactPresence.is_online ? (
                            <span className="contactPresenceOnline">
                              <span className="contactPresenceDot" />
                              online
                            </span>
                          ) : contactPresence.last_seen_at ? (
                            <span className="contactPresenceLastSeen">
                              visto por último {formatLastSeen(contactPresence.last_seen_at)}
                            </span>
                          ) : null}
                        </>
                      )}
                      {selected.attendant_name && <><span style={{ color: "var(--text-5)" }}>·</span><span>{selected.attendant_name}</span></>}
                    </span>
                  </div>
                </div>
                <div className="chatActions">
                  <button className="mobileCrmBtn btn btn-ghost btn-icon btn-sm" onClick={() => setMobilePanel(mobilePanel === "crm" ? "chat" : "crm")} aria-label="Perfil">
                    <Icon name="contact" size={16} />
                  </button>
                  {selectedSla?.tone === "critical" && <span className="badge badge-danger">Crítico</span>}
                  {selected.status !== "finalizado" && (
                    <span className="badge badge-warning" style={{ gap: 4 }}>
                      <Icon name="clock" size={10} />{conversationStatusLabel(selected.status)}
                    </span>
                  )}
                  <div style={{ width: 1, height: 20, background: "var(--border)", margin: "0 2px" }} />
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setTransferOpen(true)}
                    title="Transferir para outro atendente"
                    type="button"
                  >
                    <Icon name="transfer" size={12} /> Transferir
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={assignToMe}
                    title="Assumir este atendimento"
                    type="button"
                  >
                    <Icon name="user" size={12} /> Assumir
                  </button>
                  {/* ── Bot pause toggle ── */}
                  <button
                    className={`btn btn-sm${selected.chatbot_paused ? " btn-secondary" : " btn-secondary"}`}
                    style={selected.chatbot_paused
                      ? { color: "var(--amber)", borderColor: "var(--amber-soft)", background: "var(--amber-subtle)" }
                      : { color: "var(--accent-text)", borderColor: "var(--accent-soft)", background: "var(--accent-subtle)" }
                    }
                    onClick={async () => {
                      try {
                        const res = await api.patch(`/conversations/${selected.id}/toggle-bot`);
                        const paused = res.data?.chatbot_paused ?? (selected.chatbot_paused ? 0 : 1);
                        setConversations((prev) => prev.map((c) => c.id === selected.id ? { ...c, chatbot_paused: paused } : c));
                        setSelected((s) => ({ ...s, chatbot_paused: paused }));
                      } catch {}
                    }}
                    title={selected.chatbot_paused ? "Bot pausado nesta conversa — clique para reativar" : "Bot ativo — clique para pausar nesta conversa"}
                    type="button"
                  >
                    🤖 {selected.chatbot_paused ? "Bot pausado" : "Bot ativo"}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => changeStatus("finalizado")}
                    disabled={selected.status === "finalizado"}
                    title="Finalizar atendimento"
                    type="button"
                  >
                    <Icon name="checkCircle" size={12} /> Finalizar
                  </button>
                  {/* ── 3 pontos — menu de ações extras ── */}
                  <button
                    ref={moreBtnRef}
                    type="button"
                    className={`tb-icon-btn${moreMenuOpen ? " active" : ""}`}
                    style={{ background: "transparent", border: "none" }}
                    title="Mais opções"
                    onClick={() => {
                      if (moreBtnRef.current) {
                        const rect = moreBtnRef.current.getBoundingClientRect();
                        setMoreMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                      }
                      setMoreMenuOpen((v) => !v);
                    }}
                  >
                    <Icon name="moreV" size={14} />
                  </button>
                  {/* ── Toggle painel CRM ── */}
                  <button
                    type="button"
                    className="tb-icon-btn"
                    style={{ background: "transparent", border: "none" }}
                    title={crmPanelOpen ? "Fechar painel lateral" : "Abrir painel lateral"}
                    onClick={() => setCrmPanelOpen((v) => !v)}
                  >
                    {/* Ícone: dois painéis lado a lado */}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                      <rect x="1" y="1" width="12" height="12" rx="2"/>
                      <line x1="9" y1="1" x2="9" y2="13"/>
                    </svg>
                  </button>
                </div>
              </div>

{/* ── Search bar ── */}
              {searchOpen && (
                <div className="chatSearchBar">
                  <Icon name="search" size={13} style={{ color: "var(--text-4)", flexShrink: 0 }} />
                  <input
                    ref={searchInputRef}
                    className="chatSearchInput"
                    placeholder="Pesquisar na conversa…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <span className="chatSearchCount">
                      {messages.filter((m) => (m.message || "").toLowerCase().includes(searchQuery.toLowerCase())).length} resultado(s)
                    </span>
                  )}
                  <button type="button" className="chatSearchClose" onClick={() => setSearchOpen(false)}>
                    <Icon name="close" size={12} />
                  </button>
                </div>
              )}

              {sendNotice && <div className="sendNotice">{sendNotice}</div>}
              {templateWarningVisible && (
                <div className="sendNotice warning">
                  Essa conversa está fora da janela de 24h da Cloud API. O próximo passo ideal é enviar um template aprovado.
                </div>
              )}

              {sendMode === "whatsapp" && (templatePanelOpen || templateWarningVisible) && templates.length > 0 && (
                <section className="templateAssistBox">
                  <div className="templateAssistHeader">
                    <div>
                      <strong>Templates operacionais</strong>
                      <span>
                        {templateWarningVisible
                          ? "Escolha um template aprovado para retomar a conversa fora da janela."
                          : "Use templates prontos para follow-up, cobrança e retomada estruturada."}
                      </span>
                    </div>
                    <small>{templates.length} aprovado(s)</small>
                  </div>
                  <form className="templateAssistForm" onSubmit={sendTemplateMessage}>
                    <select
                      value={templateDraft.template_id}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        const picked = templates.find((item) => String(item.id) === String(nextId));
                        setTemplateDraft({
                          template_id: nextId,
                          variables: formatTemplateVariables(picked?.example_values || []),
                        });
                      }}
                    >
                      <option value="">Selecione um template aprovado</option>
                      {templates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} · {item.language_code}
                        </option>
                      ))}
                    </select>
                    <input
                      value={templateDraft.variables}
                      onChange={(e) => setTemplateDraft((old) => ({ ...old, variables: e.target.value }))}
                      placeholder="Variáveis separadas por vírgula"
                    />
                    {selectedTemplate && (
                      <div className="templatePreview">
                        <strong>Prévia enviada</strong>
                        <p>{renderTemplatePreview(selectedTemplate.body_text, selectedTemplateVariables) || selectedTemplate.body_text}</p>
                      </div>
                    )}
                    <div className="templateAssistActions">
                      <small>Campos dinâmicos usam <code>{`{{1}}`}</code>, <code>{`{{2}}`}</code>, <code>{`{{3}}`}</code>.</small>
                      <button className="secondaryBtn" disabled={sending || !templateDraft.template_id}>
                        {sending ? "Enviando..." : "Enviar template"}
                      </button>
                    </div>
                  </form>
                </section>
              )}

              {/* Safe IA panel */}
              {aiOpen && (
                <div className="aiPanel">
                  <div className="aiPanelHeader">
                    <strong><Icon name="ai" size={13} /> Safe IA</strong>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {aiData?.temperature && (
                        <span className={`aiBadge temp-${aiData.temperature}`}>
                          {aiData.temperature === "quente" ? "🔥 Quente" : aiData.temperature === "morno" ? "🌤 Morno" : "❄️ Frio"}
                        </span>
                      )}
                      {aiData?.sentiment && (
                        <span className={`aiBadge sentiment-${aiData.sentiment}`}>
                          {aiData.sentiment === "positive" ? "😊 Positivo" : aiData.sentiment === "negative" ? "😟 Negativo" : "😐 Neutro"}
                        </span>
                      )}
                      <button className="aiChip" onClick={loadAiSuggestions} disabled={aiLoading} style={{ fontSize: 11 }}>
                        {aiLoading ? "…" : "↺ Atualizar"}
                      </button>
                      <button className="aiChip" onClick={() => setAiOpen(false)} style={{ fontSize: 11 }}>✕</button>
                    </div>
                  </div>
                  {aiLoading && <div className="aiPanelBody">Analisando conversa…</div>}
                  {!aiLoading && aiData?.summary && <div className="aiPanelBody">{aiData.summary}</div>}
                  {!aiLoading && aiData?.suggestions?.length > 0 && (
                    <div className="aiPanelActions">
                      {aiData.suggestions.map((s, i) => (
                        <button
                          key={i}
                          className="aiSuggestion"
                          onClick={() => { setText(s.replace("{agent}", user.name || "Atendente")); setAiOpen(false); }}
                          title="Clique para inserir no campo de mensagem"
                        >
                          <small>Sugestão {i + 1}</small>
                          {s.replace("{agent}", user.name || "Atendente")}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Pinned messages bar ─────────────────────────── */}
              {messages.some((m) => m.is_pinned) && (() => {
                const pinned = messages.filter((m) => m.is_pinned);
                const last = pinned[pinned.length - 1];
                return (
                  <div className="pinnedBar">
                    <Icon name="pin" size={13} />
                    <div className="pinnedBarContent">
                      <span className="pinnedBarCount">{pinned.length} fixada{pinned.length > 1 ? "s" : ""}</span>
                      <span className="pinnedBarPreview">{(last?.message || "[mídia]").slice(0, 60)}</span>
                    </div>
                    <button
                      type="button"
                      className="pinnedBarClose"
                      title="Desafixar todas"
                      onClick={async () => {
                        for (const pm of pinned) {
                          await api.patch(`/conversations/${selected.id}/messages/${pm.id}/pin`).catch(() => {});
                        }
                        setMessages((prev) => prev.map((m) => ({ ...m, is_pinned: 0 })));
                      }}
                    >
                      <Icon name="close" size={11} />
                    </button>
                  </div>
                );
              })()}

              <div
                className="messagesArea"
                ref={chatBodyRef}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
                  setIsAtBottom(atBottom);
                  if (atBottom) setScrollUnread(0);
                  if (el.scrollTop < 80 && hasMoreMsgs && !loadingOlderMsgs) {
                    loadOlderMessages();
                  }
                }}
              >
                {/* Load older messages indicator */}
                {hasMoreMsgs && (
                  <div className="loadOlderRow">
                    {loadingOlderMsgs ? (
                      <span className="loadOlderSpinner">⟳ Carregando…</span>
                    ) : (
                      <button className="loadOlderBtn" onClick={loadOlderMessages}>
                        ↑ Carregar mensagens anteriores
                      </button>
                    )}
                  </div>
                )}
                {loadingMessages && <EmptyState icon="…" title="Carregando mensagens" text="Atualizando o histórico desta conversa." />}
                {!loadingMessages && messages.length === 0 && <EmptyState illustration="chat" title="Nenhuma mensagem ainda" text="Assim que houver troca com o cliente, as mensagens aparecem aqui." />}
                {!loadingMessages && (() => {
                  // Filter by search if active
                  const visibleMsgs = searchQuery.trim()
                    ? messages.filter((m) => (m.message || "").toLowerCase().includes(searchQuery.toLowerCase()))
                    : messages;

                  // Build date label for a message
                  function getMsgDateKey(msg) {
                    if (!msg.created_at) return "";
                    return parseDBDate(msg.created_at).toLocaleDateString("pt-BR");
                  }
                  function getMsgDateLabel(msg) {
                    if (!msg.created_at) return "";
                    const d = parseDBDate(msg.created_at);
                    const today = new Date();
                    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
                    const isToday = d.toDateString() === today.toDateString();
                    const isYesterday = d.toDateString() === yesterday.toDateString();
                    const dayName = isToday ? "Hoje" : isYesterday ? "Ontem" : null;
                    const dateStr = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
                    return dayName ? `${dayName} · ${dateStr}` : dateStr;
                  }

                  const items = [];
                  let lastDateKey = null;
                  for (const msg of visibleMsgs) {
                    const key = getMsgDateKey(msg);
                    if (key && key !== lastDateKey) {
                      items.push({ type: "date", key: `date-${key}`, label: getMsgDateLabel(msg) });
                      lastDateKey = key;
                    }
                    items.push({ type: "msg", key: msg.id, msg });
                  }

                  return items.map((item) => {
                    if (item.type === "date") {
                      return (
                        <div key={item.key} className="msgDateChip">
                          <span>{item.label}</span>
                        </div>
                      );
                    }
                    const { msg } = item;
                    const isMatch = searchQuery.trim() && (msg.message || "").toLowerCase().includes(searchQuery.toLowerCase());
                    const isOwn   = msg.sender_type !== "cliente" && String(msg.sender_user_id) === String(user.id);
                    const isAdmin = user.role === "admin" || user.role === "master";
                    const isGroupConvMsg = !!selected?.contact_is_group;
                    // For group messages, use the per-message sender name
                    const senderLabel = msg.sender_type === "cliente"
                      ? (isGroupConvMsg && msg.group_sender_name ? msg.group_sender_name : "Cliente")
                      : (msg.sender_user_name || "Atendente");
                    const senderColor = (isGroupConvMsg && msg.group_sender_name)
                      ? getAvatarColor(msg.group_sender_name)
                      : undefined;
                    return (
                      <div
                        key={msg.id}
                        className={`msgBubbleGroup ${msg.sender_type === "cliente" ? "client" : "agent"}`}
                        onMouseEnter={() => setHoveredMsgId(msg.id)}
                        onMouseLeave={() => setHoveredMsgId(null)}
                      >
                        <div className={`msgBubbleInner`}>
                          <div className={`messageBubble ${msg.sender_type === "cliente" ? "client" : "agent"}${isAudioMessage(msg) ? " audioMsg" : ""}${isMatch ? " is-search-match" : ""}${msg.is_pinned ? " is-pinned" : ""}${msg.is_starred ? " is-starred" : ""}`}>
                            {msg.is_starred ? <span className="msgStarBadge" title="Favoritado">⭐</span> : null}
                            {msg.is_pinned  ? <span className="msgPinBadge"  title="Fixado"><Icon name="pin" size={10} /></span> : null}
                            <MessageContent msg={msg} onImageClick={setLightboxSrc} isAdmin={isAdmin} />
                            <div className="messageMeta">
                              <small style={senderColor ? { color: senderColor, fontWeight: 600 } : undefined}>
                                {senderLabel}
                                {msg.edited_at ? <em className="msgEditedLabel"> · editado</em> : null}
                              </small>
                              <small className="msgMetaTime">
                                {formatTime(msg.created_at)}
                                {msg.sender_type !== "cliente" && (
                                  <MessageStatusTick
                                    status={msg.status}
                                    isOptimistic={msg._optimistic}
                                  />
                                )}
                              </small>
                            </div>
                          </div>
                          {/* Reaction chips */}
                          {msg.reactions_json && msg.reactions_json !== "{}" && (() => {
                            try {
                              const reacts = JSON.parse(msg.reactions_json);
                              const chips = Object.entries(reacts).filter(([, users]) => users.length > 0);
                              if (!chips.length) return null;
                              return (
                                <div className={`msgReactionRow ${msg.sender_type === "cliente" ? "client" : "agent"}`}>
                                  {chips.map(([emoji, users]) => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      className={`msgReactionChip${users.some((u) => String(u) === String(user.id)) ? " mine" : ""}`}
                                      onClick={() => handleCtxReact(msg, emoji)}
                                      title={`${users.length} reação(ões) — clique para reagir/remover`}
                                    >
                                      {emoji}{users.length > 1 ? <span>{users.length}</span> : null}
                                    </button>
                                  ))}
                                </div>
                              );
                            } catch { return null; }
                          })()}
                        </div>
                        {/* Hover trigger — chevron button (hidden for deleted messages) */}
                        {!msg.is_deleted && (
                          <button
                            type="button"
                            className={`msgCtxTrigger${hoveredMsgId === msg.id || ctxMenu?.msg?.id === msg.id ? " visible" : ""}`}
                            onClick={(e) => openCtxMenu(e, msg)}
                            title="Opções"
                          >
                            <Icon name="chevronDown" size={12} />
                          </button>
                        )}
                        {/* Context dropdown — rendered inline, positioned fixed via CSS */}
                        {!msg.is_deleted && ctxMenu?.msg?.id === msg.id && (
                          <MsgContextMenu
                            ctxMenu={ctxMenu}
                            isOwn={isOwn}
                            isAdmin={isAdmin}
                            onClose={closeCtxMenu}
                            isPinned={!!msg.is_pinned}
                            isStarred={!!msg.is_starred}
                            onReact={(r) => handleCtxReact(msg, r)}
                            onReply={() => handleCtxReply(msg)}
                            onCopy={() => handleCtxCopy(msg)}
                            onDelete={() => handleCtxDelete(msg)}
                            onForward={() => setForwardModal(msg)}
                            onPin={() => handleCtxPin(msg)}
                            onFavorite={() => handleCtxStar(msg)}
                            onEdit={() => handleCtxEdit(msg)}
                            onInfo={() => setMsgInfoMsg(msg)}
                          />
                        )}
                      </div>
                    );
                  });
                })()}
                {/* Scroll anchor — always keeps messages at bottom */}
                {/* ── Typing indicator ── */}
                {isContactTyping && (
                  <div className="typingBubbleWrap">
                    <div className="typingBubble">
                      <span className="typingDot" />
                      <span className="typingDot" />
                      <span className="typingDot" />
                    </div>
                    <span className="typingLabel">{selected?.contact_name?.split(" ")[0] || "Cliente"} está digitando…</span>
                  </div>
                )}
                {/* ── Scroll-to-bottom floating button ── */}
                {!isAtBottom && (
                  <button
                    type="button"
                    className="scrollToBottomBtn"
                    onClick={() => {
                      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
                      setScrollUnread(0);
                      setIsAtBottom(true);
                    }}
                    title="Rolar para as mensagens mais recentes"
                  >
                    <Icon name="chevronDown" size={16} />
                    {scrollUnread > 0 && (
                      <span className="scrollToBottomBadge">
                        {scrollUnread > 99 ? "99+" : scrollUnread}
                      </span>
                    )}
                  </button>
                )}
                <div ref={messagesEndRef} className="messagesAnchor" />
              </div>

              {/* Attach file preview bar */}
              {attachFile && (
                <div className="attachPreviewBar">
                  {attachFile.isImage ? (
                    <img src={attachFile.previewUrl} alt="preview" className="attachPreviewThumb" />
                  ) : (
                    <div style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--blue-subtle)", borderRadius: 6, flexShrink: 0 }}>
                      <Icon name="attach" size={20} />
                    </div>
                  )}
                  <div className="attachPreviewInfo">
                    <strong>{attachFile.file.name}</strong>
                    <span>{(attachFile.file.size / 1024).toFixed(0)} KB · {attachFile.file.type}</span>
                  </div>
                  <button type="button" className="attachCancelBtn" onClick={cancelAttach} title="Cancelar">
                    <Icon name="close" size={14} />
                  </button>
                  <button type="button" className="attachSendBtn" onClick={sendAttachedFile} disabled={sendingFile}>
                    {sendingFile ? "Enviando…" : "Enviar"}
                  </button>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={attachInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                style={{ display: "none" }}
                onChange={handleAttachChange}
              />

              <AudioRecorderBar recorder={audioRecorder} onSend={sendAudio} sending={sending} />

              {/* ── Edit mode bar ── */}
              {editingMsg && (
                <div className="editModeBar">
                  <Icon name="edit" size={13} />
                  <div className="editModeContent">
                    <span className="editModeLabel">Editando mensagem</span>
                    <span className="editModePreview">{(editingMsg.message || "").slice(0, 60)}</span>
                  </div>
                  <button type="button" className="editModeCancelBtn" onClick={cancelEdit} title="Cancelar edição">
                    <Icon name="close" size={13} />
                  </button>
                </div>
              )}

              {/* ── Reply quote bar ── */}
              {replyTo && (
                <div className="replyQuoteBar">
                  <div className="replyQuoteAccent" />
                  <div className="replyQuoteContent">
                    <span className="replyQuoteSender">
                      {replyTo.sender_type === "cliente" ? "Cliente" : (replyTo.sender_user_name || "Atendente")}
                    </span>
                    <span className="replyQuoteText">
                      {isAudioMessage(replyTo)
                        ? "🎙 Áudio"
                        : (replyTo.message || "[mídia]").substring(0, 100) + ((replyTo.message || "").length > 100 ? "…" : "")}
                    </span>
                  </div>
                  <button type="button" className="replyQuoteClose" onClick={() => setReplyTo(null)} title="Cancelar resposta">
                    <Icon name="close" size={13} />
                  </button>
                </div>
              )}

              <form className="sendBox" onSubmit={sendMessage}>
                <div className="composerToolbar">
                  {/* ── Botões esquerdos ── */}
                  <div className="emojiPickerWrap">
                    <button
                      type="button"
                      className={`composerBtn composerBtn--icon${emojiPickerOpen ? " is-active" : ""}`}
                      onClick={() => setEmojiPickerOpen((v) => !v)}
                      title="Inserir emoji"
                    >
                      <Icon name="emoji" size={15} />
                    </button>
                    {emojiPickerOpen && (
                      <>
                        <div className="emojiPickerBackdrop" onClick={() => setEmojiPickerOpen(false)} />
                        <div className="emojiPickerPopover">
                          <EmojiPicker
                            onEmojiClick={(data) => { insertEmoji(data); setEmojiPickerOpen(false); }}
                            emojiStyle={EmojiStyle.NATIVE}
                            theme={EmojiTheme.LIGHT}
                            width={320}
                            height={400}
                            searchPlaceHolder="Buscar emoji"
                            previewConfig={{ showPreview: false }}
                            skinTonesDisabled
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    className={`composerBtn composerBtn--icon${attachFile ? " is-active" : ""}`}
                    onClick={handleAttachClick}
                    title={attachFile ? "Arquivo selecionado — clique para trocar" : "Enviar imagem, PDF ou documento"}
                  >
                    <Icon name="attach" size={15} />
                  </button>
                  <button
                    type="button"
                    className={`composerBtn composerBtn--icon${templatePanelOpen ? " is-active" : ""}`}
                    onClick={() => setTemplatePanelOpen((v) => !v)}
                    title="Templates de mensagem"
                  >
                    <Icon name="rule" size={15} />
                  </button>
                  <button
                    type="button"
                    className={`composerBtn composerBtn--icon${quickReplyOpen ? " is-active" : ""}`}
                    onClick={() => setQuickReplyOpen((current) => !current)}
                    title="Respostas rápidas (ou digite /atalho)"
                  >
                    <Icon name="zap" size={15} />
                  </button>
                  {favoriteQuickReplies.slice(0, 3).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="composerBtn composerBtn--shortcut"
                      onClick={() => applyQuickReply(item)}
                      title={item.title}
                    >
                      /{item.shortcut || item.title?.slice(0, 6)}
                    </button>
                  ))}

                  {/* ── Spacer ── */}
                  <span style={{ flex: 1 }} />

                  {/* ── Direita: janela de 24h + toggle Safe IA ── */}
                  {windowStatus && (
                    <span className={`composerWindowLabel${windowTone === "danger" ? " is-closed" : ""}`}>
                      {windowStatus.label}
                    </span>
                  )}
                  <label className="composerIaToggle" title="Safe IA — sugestões de resposta">
                    <input
                      type="checkbox"
                      checked={aiOpen}
                      onChange={() => aiOpen ? setAiOpen(false) : loadAiSuggestions()}
                    />
                    <span className="composerIaToggle-track">
                      <span className="composerIaToggle-thumb" />
                    </span>
                  </label>
                  <span className="composerIaLabel">Safe IA</span>
                </div>
                {quickReplyOpen && suggestedQuickReplies.length > 0 && (
                  <div className="quickReplySuggestionStrip">
                    <small>Sugestões para esta conversa</small>
                    <div className="quickReplySuggestionList">
                      {suggestedQuickReplies.map((item) => (
                        <button key={item.id} type="button" className="quickReplySuggestionChip" onClick={() => applyQuickReply(item)}>
                          <strong>{item.title}</strong>
                          <span>{item.shortcut ? `/${item.shortcut}` : item.category}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {quickReplyOpen && (
                  <div className="quickReplyPanel">
                    <div className="quickReplyPanelTop">
                      <strong>Respostas rápidas</strong>
                      <span>{filteredQuickReplies.length} disponível(is)</span>
                    </div>
                    {favoriteQuickReplies.length > 0 && (
                      <div className="quickReplyFavorites">
                        <small>Favoritas</small>
                        <div className="quickReplyFavoriteList">
                          {favoriteQuickReplies.map((item) => (
                            <button key={item.id} type="button" className="quickReplyFavoriteChip" onClick={() => applyQuickReply(item)}>
                              <strong>{item.title}</strong>
                              <span>{item.shortcut ? `/${item.shortcut}` : `${item.usage_count || 0} uso(s)`}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="quickReplyPanelControls">
                      <input
                        value={quickReplySearch}
                        onChange={(e) => setQuickReplySearch(e.target.value)}
                        placeholder="Buscar por título, atalho ou conteúdo"
                      />
                      <select value={quickReplyCategory} onChange={(e) => setQuickReplyCategory(e.target.value)}>
                        {quickReplyCategories.map((item) => (
                          <option key={item} value={item}>{item === "all" ? "Todas as categorias" : item}</option>
                        ))}
                      </select>
                    </div>
                    <div className="quickReplyList">
                      {filteredQuickReplies.length === 0 && (
                        <EmptyState illustration="search" title="Nenhuma resposta rápida encontrada" text="Cadastre snippets em Configurações ou ajuste a busca." />
                      )}
                      {filteredQuickReplies.map((item) => (
                        <button key={item.id} type="button" className="quickReplyCard" onClick={() => applyQuickReply(item)}>
                          <div className="quickReplyCardHead">
                            <strong>{item.title}</strong>
                            {item.shortcut ? <em>/{item.shortcut}</em> : <span>{item.category}</span>}
                          </div>
                          <p>{renderQuickReplyBody(item.body_text, quickReplyContext)}</p>
                          <div className="quickReplyCardMeta">
                            <small>{item.category}</small>
                            <small>{item.usage_count || 0} uso(s)</small>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {quickReplyShortcutMatches.length > 0 && (
                  <div className="quickReplyInlineMenu">
                    <div className="quickReplyInlineMenuHead">
                      <strong>Atalhos rápidos</strong>
                      <span>Pressione Tab ou Enter para inserir</span>
                    </div>
                    <div className="quickReplyInlineList">
                      {quickReplyShortcutMatches.map((item, index) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`quickReplyInlineOption ${index === quickReplyHighlightIndex ? "active" : ""}`}
                          onClick={() => applyQuickReply(item, { replaceCommand: true })}
                        >
                          <div>
                            <strong>{item.title}</strong>
                            <span>{item.shortcut ? `/${item.shortcut}` : item.category}</span>
                          </div>
                          <small>{item.category}</small>
                        </button>
                      ))}
                    </div>
                    {shortcutPreviewReply && (
                      <p className="quickReplyInlinePreview">
                        {renderQuickReplyBody(shortcutPreviewReply.body_text, quickReplyContext)}
                      </p>
                    )}
                  </div>
                )}
                {fmtBarVisible && (
                  <div className="fmtBar">
                    <button type="button" className="fmtBtn" title="Negrito (Ctrl+B)" onMouseDown={(e) => { e.preventDefault(); applyFormat("bold"); }}>
                      <strong>B</strong>
                    </button>
                    <button type="button" className="fmtBtn" title="Itálico (Ctrl+I)" onMouseDown={(e) => { e.preventDefault(); applyFormat("italic"); }}>
                      <em>I</em>
                    </button>
                    <button type="button" className="fmtBtn" title="Riscado" onMouseDown={(e) => { e.preventDefault(); applyFormat("strike"); }}>
                      <del>S</del>
                    </button>
                    <button type="button" className="fmtBtn" title="Código (Ctrl+E)" onMouseDown={(e) => { e.preventDefault(); applyFormat("code"); }}>
                      <span style={{ fontFamily: "monospace", fontSize: 12 }}>{"<>"}</span>
                    </button>
                    <button type="button" className="fmtBtn" title="Citação" onMouseDown={(e) => { e.preventDefault(); applyFormat("quote"); }}>
                      <span>❝</span>
                    </button>
                    <div className="fmtBarDivider" />
                    <span className="fmtBarHint">Ctrl+B · Ctrl+I · Ctrl+E</span>
                  </div>
                )}
                <div className="composerRow">
                  <textarea
                    ref={composerTextareaRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleComposerKeyDown}
                    onSelect={handleTextareaSelect}
                    onMouseUp={handleTextareaSelect}
                    onBlur={() => setFmtBarVisible(false)}
                    placeholder={sendMode === "whatsapp" ? "Digite a mensagem que será enviada ao cliente..." : "Digite uma anotação ou resposta interna..."}
                    disabled={sending || whatsappSendDisabled}
                    rows={3}
                  />
                  <div className="composerRowActions">
                    <button
                      type="button"
                      className={`composerAudioBtn${audioRecorder.state !== "idle" ? " is-recording" : ""}`}
                      onClick={audioRecorder.state === "idle" ? audioRecorder.start : audioRecorder.stop}
                      title={audioRecorder.state === "idle" ? "Gravar áudio" : "Parar gravação"}
                    >
                      <Icon name={audioRecorder.state === "recording" ? "stop" : "mic"} size={15} />
                    </button>
                    <button
                      className="btn btn-primary btn-lg"
                      disabled={sending || !text.trim() || whatsappSendDisabled}
                    >
                      <Icon name="send" size={14} />
                      {sending ? "Enviando..." : "Enviar"}
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>

        <div className={`crmSidePanelWrapper${mobilePanel !== "crm" ? " mobileHidden" : ""}${!crmPanelOpen ? " crmPanelHidden" : ""}`}>
          <button className="mobileCrmBackBtn" onClick={() => setMobilePanel("chat")} aria-label="Voltar ao chat">
            <Icon name="chevronRight" size={18} /> Chat
          </button>
          <CustomerSidePanel
            api={api}
            user={user}
            conversation={selected}
            contact={selectedContact}
            messages={messages}
            users={users}
            refreshToken={panelRefreshToken}
            onNavigate={setActivePage}
            onArchive={() => setArchiveOpen(true)}
            onReload={async () => {
              if (selected) await loadContactForConversation(selected);
              await loadSummary();
            }}
          />
        </div>
      </section>

      {/* Message info modal */}
      <MsgInfoModal msg={msgInfoMsg} onClose={() => setMsgInfoMsg(null)} />

      {/* Forward modal */}
      {forwardModal && (
        <ForwardModal
          msg={forwardModal}
          conversations={conversations}
          onForward={handleForward}
          onClose={() => setForwardModal(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmMsg && (
        <div className="modal-overlay-v2">
          <div className="modal-v2 modal-sm">
            <div className="modal-header">
              <h3>Apagar mensagem?</h3>
              <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => setDeleteConfirmMsg(null)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-2)", marginBottom: 8 }}>
                Esta mensagem será marcada como apagada para todos na conversa. A ação não pode ser desfeita.
              </p>
              {deleteConfirmMsg.message && (
                <div className="deleteConfirmPreview">
                  {(deleteConfirmMsg.message || "").slice(0, 120)}
                  {(deleteConfirmMsg.message || "").length > 120 ? "…" : ""}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger btn-sm" onClick={confirmDelete}>
                🗑 Apagar mensagem
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeleteConfirmMsg(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox de imagem */}
      {lightboxSrc && (
        <div
          className="lightboxOverlay"
          onClick={() => setLightboxSrc(null)}
          onKeyDown={(e) => e.key === "Escape" && setLightboxSrc(null)}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <button
            className="lightboxClose"
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            title="Fechar"
          >
            ✕
          </button>
          <img
            src={lightboxSrc}
            alt="visualização"
            className="lightboxImg"
            onClick={(e) => e.stopPropagation()}
          />
          <a
            href={lightboxSrc}
            download
            className="lightboxDownload"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon name="download" size={13} /> Baixar
          </a>
        </div>
      )}
    </>
  );
}

export default AttendancePage;
